import { useCallback, useEffect, useRef, useState } from "react";
import {
  useMutation,
  useQuery,
  useQueryClient,
  type QueryClient,
} from "@tanstack/react-query";
import {
  Navigate,
  useLocation,
  useNavigate,
  useParams,
  useSearchParams,
} from "react-router-dom";
import { useTranslation } from "react-i18next";
import toast from "react-hot-toast";
import { AlertTriangle, Ban, CalendarPlus, ChevronDown, ChevronRight, ExternalLink, Loader2, MessageSquareWarning, Paperclip, Send, ShieldCheck, Trash2, Upload, XCircle } from "lucide-react";

import { IncomeCertificateSummaryCard } from "@/components/features/notarial-requests/income-certificate-summary-card";
import { AuthenticatedBlobDocumentLink } from "@/components/shared/AuthenticatedBlobDocumentLink";
import { ConfirmationDialog } from "@/components/shared/ConfirmationDialog";
import { QuoteAwardSummary } from "@/components/features/notarial-requests/quote-award-summary";
import { setMyQuoteRequestDeliveryAddress } from "@/services/quoteRequestsService";
import { resolveQuoteProfessionalName } from "@/lib/quoteProfessionalName";
import { DocumentCorrectionCard } from "@/components/features/notarial-requests/document-correction-card";
import { EmptyState } from "@/components/shared/EmptyState";
import { RequestAlertCard } from "@/components/shared/RequestAlertCard";
import { LoadingPage } from "@/components/shared/LoadingPage";
import { QuoteRequestLegsCard } from "@/components/features/notarial-requests/quote-request-legs-card";
import { QuoteChargeSummary } from "@/components/features/notarial-requests/quote-charge-summary";
import {
  RequestActivityList,
  notarialActivityConfig,
  quoteRequestActivityConfig,
  enterpriseActivityConfig,
} from "@/components/shared/RequestActivityList";
import {
  AddressPicker,
  isAddressSelectionReady,
  type AddressSelection,
} from "@/components/shared/AddressPicker";
import { DeliveryAddressSummaryCard } from "@/components/shared/DeliveryAddressSummaryCard";
import { StatusBadge, type StatusBadgeVariant } from "@/components/shared/StatusBadge";
import { StyledMultiFileInput } from "@/components/shared/StyledMultiFileInput";
import { MAX_FILES_PER_BULK_UPLOAD } from "@/lib/uploadLimits";
import { Avatar, AvatarFallback, AvatarImage } from "@/components/ui/avatar";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { Label } from "@/components/ui/label";
import { Input } from "@/components/ui/input";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Textarea } from "@/components/ui/textarea";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import { QuoteAttachmentsTable } from "@/components/features/notarial-requests/quote-attachments-table";
import { formatDisplayDate, isPastCalendarDay } from "@/lib/dateFormat";
import { formatUyMinor } from "@/lib/formatUyMinor";
import {
  notarialDocumentTypeTranslationKey,
  type NotarialCertificateLabelContext,
} from "@/lib/notarialDocumentTypeLabel";
import { formatCertificatePricingProductWithSource } from "@/utils/certificatePricingDisplay";
import { useAuth, type AuthUser } from "@/contexts/AuthContext";
import {
  filterConsumerVisibleActivityEvents,
  getConsumerFulfillmentHintTranslationKey,
  getConsumerStatusBadgeVariant,
  getConsumerStatusTranslationKey,
  shouldHideAssignedProfessionalFromConsumer,
} from "@/utils/consumerNotarialRequestView";
import {
  collapseQuoteBroadcastEvents,
  isQuoteRequestCancellable,
  isQuotingClosed,
  isLegQuotingClosed,
  isLegQuotingOpen,
  legStatusBadgeVariant,
  getConsumerStatusBadgeVariant as getQuoteConsumerStatusBadgeVariant,
  getConsumerStatusTranslationKey as getQuoteConsumerStatusTranslationKey,
} from "@/utils/consumerQuoteRequestView";
import {
  cancelMyAccountantRequest,
  deleteMyAccountantRequestDocument,
  fetchMyAccountantRequestDocumentBlob,
  getMyConsumerCertificateRequestDetail,
  parseCertificateRequestSourceParam,
  resubmitMyAccountantRequest,
  submitMyAccountantRequestDeliveryDetails,
  uploadMyAccountantRequestDocumentForSlot,
  getMyAccountantRequestDocumentSlots,
  type ConsumerCertificateRequestDetail,
  type ConsumerCertificateRequestSource,
  uploadMyRequestDocumentsBulk as uploadAccountantDocumentsBulk,
} from "@/services/userAccountantRequestsService";
import {
  addQuoteRequestAttachment,
  answerQuoteRequestClarification,
  awardMyQuoteRequestQuote,
  cancelMyQuoteRequest,
  contactDetailKindsFromError,
  createMyQuoteRequestCheckout,
  deleteQuoteRequestAttachment,
  downloadQuoteRequestAttachment,
  extendMyQuoteRequest,
  getMyQuoteRequestDetail,
  listMyQuoteRequestQuotes,
  rejectMyQuoteRequestQuote,
} from "@/services/quoteRequestsService";
import {
  QuoteReviewCard,
  QuoteReviewColumnHeader,
} from "@/components/features/notarial-requests/quote-review-card";
import { QuoteClarificationsCard } from "@/components/features/notarial-requests/quote-clarifications-card";
import { toastIfCheckoutAutoPaidBypass } from "@/utils/certificatePaymentFlow";
import {
  type UserNotarialRequestDocument,
  cancelMyNotarialRequest,
  deleteMyNotarialRequestDocument,
  fetchMyNotarialRequestDocumentBlob,
  resubmitMyNotarialRequest,
  submitMyNotarialRequestDeliveryDetails,
  uploadMyNotarialRequestDocumentForSlot,
  uploadMyRequestDocumentsBulk as uploadNotarialDocumentsBulk,
  getMyNotarialRequestDocumentSlots,
  type RequestDocumentSlot,
  type UserNotarialRequestDocumentType,
} from "@/services/userNotarialRequestsService";
import {
  documentTypesForRequestType,
  type RequestDocumentTypeSlug,
} from "@gestionalo/shared-types";
import { QuoteDeliveryAddressStep } from "@/components/features/notarial-requests/quote-delivery-address-step";

/* The portal half of the drift guard the backend entity also carries: every
   slug the shared mapping can hand back must be a type this app knows how to
   label and upload. A slug added to @gestionalo/shared-types that this union
   lacks fails the portal build rather than rendering an unlabelled slot. */
type _SharedSlugsAreKnownHere = RequestDocumentTypeSlug extends UserNotarialRequestDocumentType
  ? true
  : ["@gestionalo/shared-types offers a document type the portal cannot label"];
const _sharedSlugsAreKnownHere: _SharedSlugsAreKnownHere = true;
void _sharedSlugsAreKnownHere;
import {
  createUserAddress,
  listUserAddresses,
  type UserAddressCreatePayload,
  type UserAddressDto,
} from "@/services/userAddressesService";
import { useAssignedProfessionalAvatarObjectUrl } from "@/hooks/useAssignedProfessionalAvatarObjectUrl";
import {
  getMyEnterpriseRequest,
  getMyEnterpriseRequestEvents,
  completeMyEnterpriseRequestStep,
  uploadEnterpriseRequestDocument,
  uploadEnterpriseRequestBlob,
  cancelMyEnterpriseRequest,
  replyToEnterpriseInfoRequest,
  fetchMyEnterpriseDocumentBlob,
} from "@/services/enterpriseCatalogService";
import { Collapsible, CollapsibleContent, CollapsibleTrigger } from "@/components/ui/collapsible";
import { CompletedStepsSummary } from "@/components/features/enterprise/CompletedStepsSummary";
import { GraphWorkflowRunner } from "@/components/features/enterprise/GraphWorkflowRunner";
import { getActorLabel } from "@gestionalo/enterprise-graph";
import type { GraphState, ProductGraph, PricingNodeData, InviteActorsNodeData } from "@gestionalo/enterprise-graph";
import { evaluateRateTable } from "@/utils/enterprisePricingEngine";
import { openAuthenticatedBlobInNewTab } from "@/utils/openAuthenticatedBlobInNewTab";
import { DetailRow, DetailRows } from "@/components/shared/DetailRows";
import { RequestDetailLayout } from "@/components/shared/RequestDetailLayout";
import { DocumentSlotsList } from "@/components/features/notarial-requests/document-slots-list";
import { ReferenceSection } from "@/components/shared/ReferenceSection";

const PODER_JUDICIAL_CONSULTA_URL =
  "https://profesionales.poderjudicial.gub.uy/tyj_dmz/servlet/consultaprofesionales";

const ACCOUNTANT_CJPP_CONSULTA_URL =
  "https://consultas.cjppu.org.uy/webcafi/consulta_libre.php";

/**
 * quote_request statuses where the BIDS are still the consumer's business.
 *
 * Deliberately stops at 'awarded'. Once payment clears, the auction is over
 * and each discipline has a real trámite with its own page — continuing to
 * show "Cotizaciones recibidas", with rejected bids and an award badge,
 * presents a decision that has already been made as if it were still
 * pending, on a page whose actual subject is now the work.
 *
 * 'awarded' is included because that is the window between choosing and
 * paying, where the consumer legitimately still wants to see what they chose.
 */
const QUOTE_STATUSES_WITH_POSSIBLE_BIDS = ["quoting", "awarded"];

function invalidateConsumerCertificateCaches(queryClient: QueryClient, requestId: string): void {
  void queryClient.invalidateQueries({ queryKey: ["me", "certificate-requests"] });
  void queryClient.invalidateQueries({ queryKey: ["me", "certificate-requests", "detail", requestId] });
  void queryClient.invalidateQueries({ queryKey: ["me", "notarial-requests"] });
  void queryClient.invalidateQueries({ queryKey: ["me", "notarial-requests", requestId] });
  void queryClient.invalidateQueries({ queryKey: ["me", "accountant-requests"] });
  void queryClient.invalidateQueries({ queryKey: ["me", "accountant-requests", requestId] });
}

function buildDeliverySummaryProps(
  request: ConsumerCertificateRequestDetail,
  bookAddress: UserAddressDto | null,
): {
  formattedAddress: string;
  label?: string | null;
  isDefault: boolean;
  contactPhone: string | null;
  contactPersonName: string | null;
  courierNotes: string | null;
} {
  const formatted =
    bookAddress?.formattedAddress?.trim() || request.deliveryAddressSnapshot?.trim() || "";
  return {
    formattedAddress: formatted,
    label: bookAddress?.label ?? null,
    isDefault: bookAddress?.isDefault ?? false,
    contactPhone:
      request.deliveryContactPhone?.trim() || bookAddress?.contactPhone?.trim() || null,
    contactPersonName:
      request.deliveryContactName?.trim() || bookAddress?.contactPersonName?.trim() || null,
    courierNotes: request.deliveryNotes?.trim() || bookAddress?.courierNotes?.trim() || null,
  };
}

function getProtectedDocumentTypesForRequestType(
  requestType: string,
): Set<UserNotarialRequestDocumentType> {
  if (requestType === "notarial_legal_entity") {
    return new Set<UserNotarialRequestDocumentType>([
      "estatuto",
      "comunicacion_bcu",
      "legal_document_front",
      "legal_document_back",
      "passport_document",
    ]);
  }
  return new Set<UserNotarialRequestDocumentType>();
}

/**
 * A server-supplied error message, or the given fallback.
 *
 * Nest puts a BadRequestException's message on `response.data.message`. A
 * mutation that discards it and shows "try again" turns every distinct,
 * actionable refusal into the same dead end.
 */
function serverMessageOr(err: unknown, fallback: string): string {
  const data =
    err !== null &&
    typeof err === "object" &&
    "response" in err &&
    (err as { response?: { data?: unknown } }).response != null
      ? (err as { response: { data?: unknown } }).response.data
      : undefined;
  if (data !== null && typeof data === "object" && "message" in data) {
    const message = (data as { message?: unknown }).message;
    if (typeof message === "string" && message.trim() !== "") return message;
  }
  return fallback;
}

function AssignedProfessionalCard({
  requestId,
  requestSource,
  requestType,
  professional,
}: {
  requestId: string;
  requestSource: ConsumerCertificateRequestSource;
  requestType: string;
  professional: {
    firstName: string | null;
    lastName: string | null;
    email: string;
    profileImage: string | null;
    cedula: string | null;
    notaryMatricula: string | null;
  };
}): JSX.Element {
  const { t } = useTranslation();
  const [professionalDialogOpen, setProfessionalDialogOpen] = useState(false);
  const isNotaryRequest = requestType !== "income_certificate";
  const avatarObjectUrl = useAssignedProfessionalAvatarObjectUrl(
    requestId,
    requestSource,
    professional.profileImage,
  );

  const initials =
    [professional.firstName, professional.lastName]
      .filter(Boolean)
      .map((name) => name![0].toUpperCase())
      .join("") || professional.email[0].toUpperCase();
  const displayName =
    [professional.firstName, professional.lastName].filter(Boolean).join(" ").trim() ||
    professional.email;
  const cedulaDisplay = professional.cedula?.trim() || t("requests.user.sourceNotAvailable");
  const matriculaDisplay =
    professional.notaryMatricula?.trim() || t("requests.user.sourceNotAvailable");

  const openProfessionalDialog = (): void => {
    setProfessionalDialogOpen(true);
  };

  return (
    <>
      <Card className="overflow-hidden border border-border shadow-sm">
        <CardContent className="p-0">
          <div className="flex gap-0 border-l-4 border-l-green-600 dark:border-l-green-500">
            <div
              role="button"
              tabIndex={0}
              className="min-w-0 flex-1 cursor-pointer space-y-4 rounded-md p-4 outline-none transition-colors duration-150 motion-reduce:transition-none hover:bg-muted/40 focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2 focus-visible:ring-offset-background"
              onClick={openProfessionalDialog}
              onKeyDown={(e) => {
                if (e.key === "Enter" || e.key === " ") {
                  e.preventDefault();
                  openProfessionalDialog();
                }
              }}
              aria-label={t("requests.user.professionalDetailOpenLabel")}
            >
              <div className="flex items-center gap-3">
                <p className="shrink-0 text-xs font-medium uppercase tracking-wide text-muted-foreground">
                  {isNotaryRequest
                    ? t("requests.user.assignedNotaryLabel")
                    : t("requests.user.assignedAccountantLabel")}
                </p>
                <Avatar className="h-9 w-9 shrink-0 ring-2 ring-background shadow-sm">
                  {avatarObjectUrl ? (
                    <AvatarImage src={avatarObjectUrl} alt={displayName} />
                  ) : null}
                  <AvatarFallback className="bg-primary/10 text-sm font-medium text-primary">
                    {initials}
                  </AvatarFallback>
                </Avatar>
                <span className="min-w-0 flex-1 truncate text-sm font-semibold text-foreground">
                  {displayName}
                </span>
                <div
                  className="flex h-9 w-9 shrink-0 items-center justify-center rounded-full bg-green-600 text-white dark:bg-green-500"
                  title={
                    isNotaryRequest
                      ? t("requests.user.assignedNotaryVerifiedLegend")
                      : t("requests.user.assignedAccountantVerifiedLegend")
                  }
                >
                  <ShieldCheck className="h-5 w-5" />
                </div>
              </div>

              <div className="flex items-start gap-2 rounded-md bg-muted/50 px-3 py-2 dark:bg-muted/30">
                <ShieldCheck className="mt-0.5 h-4 w-4 shrink-0 text-green-600 dark:text-green-400" />
                <p className="text-xs leading-relaxed text-muted-foreground">
                  {isNotaryRequest
                    ? t("requests.user.assignedNotaryVerifiedLegend")
                    : t("requests.user.assignedAccountantVerifiedLegend")}
                </p>
              </div>

              <p className="text-center text-xs text-muted-foreground">
                {t("requests.user.professionalDetailOpenHint")}
              </p>
            </div>
          </div>
        </CardContent>
      </Card>

      <Dialog open={professionalDialogOpen} onOpenChange={setProfessionalDialogOpen}>
        <DialogContent className="max-h-[90vh] w-[calc(100vw-2rem)] max-w-lg overflow-y-auto p-4 sm:p-6">
          <DialogHeader>
            <DialogTitle>{t("requests.user.professionalDetailTitle")}</DialogTitle>
            <DialogDescription>{t("requests.user.professionalDetailDescription")}</DialogDescription>
          </DialogHeader>
          <div className="space-y-6 py-2">
            <div className="flex flex-col items-center gap-4 sm:flex-row sm:items-start">
              <Avatar className="h-28 w-28 shrink-0 ring-2 ring-border shadow-md motion-reduce:transition-none sm:h-32 sm:w-32">
                {avatarObjectUrl ? (
                  <AvatarImage src={avatarObjectUrl} alt={displayName} className="object-cover" />
                ) : null}
                <AvatarFallback className="bg-primary/10 text-3xl font-semibold text-primary sm:text-4xl">
                  {initials}
                </AvatarFallback>
              </Avatar>
              <div className="min-w-0 flex-1 space-y-3 text-center sm:text-left">
                <p className="text-xs font-medium uppercase tracking-wide text-muted-foreground">
                  {isNotaryRequest
                    ? t("requests.user.assignedNotaryLabel")
                    : t("requests.user.assignedAccountantLabel")}
                </p>
                <p className="text-lg font-semibold leading-snug text-foreground">{displayName}</p>
                <div className="flex items-start gap-2 rounded-md bg-muted/50 px-3 py-2 text-left dark:bg-muted/30">
                  <ShieldCheck className="mt-0.5 h-4 w-4 shrink-0 text-green-600 dark:text-green-400" />
                  <p className="text-xs leading-relaxed text-muted-foreground">
                    {isNotaryRequest
                      ? t("requests.user.assignedNotaryVerifiedLegend")
                      : t("requests.user.assignedAccountantVerifiedLegend")}
                  </p>
                </div>
              </div>
            </div>

            {isNotaryRequest ? (
              <div className="space-y-4 border-t border-border pt-4">
                <p className="text-sm font-medium text-foreground">
                  {t("requests.user.professionalDetailRegistrySectionTitle")}
                </p>
                <p className="text-xs text-muted-foreground">{t("requests.user.sourceDialogDescription")}</p>
                <div className="grid gap-1.5">
                  <Label className="text-xs text-muted-foreground">
                    {t("requests.user.sourceCedulaLabel")}
                  </Label>
                  <p className="rounded bg-muted/50 px-2 py-1.5 font-mono text-sm">{cedulaDisplay}</p>
                </div>
                <div className="grid gap-1.5">
                  <Label className="text-xs text-muted-foreground">
                    {t("requests.user.sourceMatriculaLabel")}
                  </Label>
                  <p className="rounded bg-muted/50 px-2 py-1.5 font-mono text-sm">{matriculaDisplay}</p>
                </div>
                <a
                  href={PODER_JUDICIAL_CONSULTA_URL}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="inline-flex items-center gap-2 text-sm font-medium text-primary transition-colors duration-150 hover:underline"
                >
                  {t("requests.user.sourceLinkLabel")}
                  <ExternalLink className="h-3.5 w-3.5 shrink-0" />
                </a>
              </div>
            ) : (
              <div className="space-y-3 border-t border-border pt-4">
                <p className="text-sm font-medium text-foreground">
                  {t("requests.user.professionalDetailAccountantRegistrySectionTitle")}
                </p>
                <p className="text-xs leading-relaxed text-muted-foreground">
                  {t("requests.user.professionalDetailAccountantVerifyBody")}
                </p>
                <a
                  href={ACCOUNTANT_CJPP_CONSULTA_URL}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="inline-flex items-center gap-2 text-sm font-medium text-primary transition-colors duration-150 hover:underline"
                >
                  {t("requests.user.professionalDetailAccountantVerifyLinkLabel")}
                  <ExternalLink className="h-3.5 w-3.5 shrink-0" />
                </a>
              </div>
            )}
          </div>
        </DialogContent>
      </Dialog>
    </>
  );
}

function defaultDeliveryRecipientDisplayName(user: AuthUser | null): string {
  if (!user) return "";
  const fromParts = [user.firstName, user.lastName].filter(Boolean).join(" ").trim();
  return fromParts || user.displayName?.trim() || "";
}

function EnterpriseRequestDetailView({
  er,
  graph,
  state,
  statusVariant,
  statusLabelKey,
  onCompleteStep,
  onBack,
  onCancel,
  isCancelling,
  t,
}: {
  er: import("@/services/enterpriseCatalogService").EnterpriseRequest;
  graph: ProductGraph | null;
  state: GraphState | null;
  statusVariant: StatusBadgeVariant;
  statusLabelKey: string;
  onCompleteStep: (nodeId: string, data: Record<string, unknown>) => Promise<void>;
  onBack: () => void;
  onCancel: () => Promise<void>;
  isCancelling: boolean;
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  t: any;
}): JSX.Element {
  const [cancelOpen, setCancelOpen] = useState(false);
  const [replyMessage, setReplyMessage] = useState("");
  const [replyAttachments, setReplyAttachments] = useState<
    Array<{ file: File; label: string }>
  >([]);
  const queryClient = useQueryClient();
  const eventsQuery = useQuery({
    queryKey: ["me", "enterprise-requests", "events", er.id],
    queryFn: () => getMyEnterpriseRequestEvents(er.id),
  });

  const replyMutation = useMutation({
    mutationFn: async () => {
      // Upload attachments first
      let uploadedAttachments: Array<{ label: string; fileUrl: string; fileName: string; fileMimeType: string }> | undefined;
      if (replyAttachments.length > 0) {
        uploadedAttachments = [];
        for (const att of replyAttachments) {
          const result = await uploadEnterpriseRequestBlob(er.id, att.file);
          uploadedAttachments.push({
            label: att.label,
            fileUrl: result.fileUrl,
            fileName: result.fileName,
            fileMimeType: result.fileMimeType,
          });
        }
      }
      await replyToEnterpriseInfoRequest(er.id, replyMessage, uploadedAttachments);
    },
    onSuccess: () => {
      setReplyMessage("");
      setReplyAttachments([]);
      void queryClient.invalidateQueries({ queryKey: ["me", "enterprise-requests"] });
      void queryClient.invalidateQueries({ queryKey: ["me", "enterprise-requests", "events", er.id] });
      toast.success(t("requests.user.enterprise.replySent"));
    },
    onError: () => {
      toast.error(t("requests.user.enterprise.replyError"));
    },
  });

  // Resolve participant scope: if current user is a participant invited via an inviteActors node,
  // check that node's participantScope to decide what context sections to show.
  const myInviteNodeId = er.myParticipant?.inviteNodeId ?? null;
  const participantScope: 'full' | 'limited' = (() => {
    if (!myInviteNodeId || !graph) return 'full';
    const inviteNode = graph.nodes.find((n) => n.id === myInviteNodeId && n.type === "inviteActors");
    if (!inviteNode) return 'full';
    return (inviteNode.data as InviteActorsNodeData).participantScope ?? 'full';
  })();
  const isLimitedScope = participantScope === 'limited';

  const TERMINAL_ENTERPRISE_STATUSES = ["approved", "rejected", "cancelled"];
  const canCancel = !TERMINAL_ENTERPRISE_STATUSES.includes(er.status);

  const rejectionEvent = er.status === "rejected"
    ? (eventsQuery.data ?? []).find((e) => e.type === "rejected")
    : null;
  const rejectionReason = rejectionEvent?.data?.reason as string | undefined;

  const infoRequestEvent = er.status === "needs_info"
    ? (eventsQuery.data ?? []).filter((e) => e.type === "info_requested").pop()
    : null;
  const infoRequestMessage = infoRequestEvent?.data?.message as string | undefined;

  // Extract uploaded documents from payload, ordered most recent first
  const enterprisePayload = er.payload as Record<string, unknown> | null;
  const uploadedDocuments = enterprisePayload && graph && state
    ? Object.entries(enterprisePayload).flatMap(([nId, nodeData]) => {
        if (!nodeData || typeof nodeData !== "object" || Array.isArray(nodeData)) return [];
        const d = nodeData as Record<string, unknown>;
        if (d.fileUrl && d.fileName) {
          // Resolve actor-keyed IDs (e.g. "internal-cert_0") to base node + actor suffix
          const actorMatch = nId.match(/^(.+)_(\d+)$/);
          const baseId = actorMatch ? actorMatch[1] : nId;
          const actorIdx = actorMatch ? Number(actorMatch[2]) : null;
          const node = graph.nodes.find((n) => n.id === baseId) ?? graph.nodes.find((n) => n.id === nId);
          const baseLabel = (node?.data as { label?: string } | undefined)?.label ?? baseId;
          const actorLabel = getActorLabel(graph);
          const nodeLabel = actorIdx !== null
            ? `${baseLabel} — ${actorLabel} ${actorIdx + 1}`
            : baseLabel;
          const pathIndex = state.completedPath.indexOf(baseId);
          return [{ nodeId: nId, nodeLabel, fileName: d.fileName as string, pathIndex }];
        }
        return [];
      }).sort((a, b) => b.pathIndex - a.pathIndex)
    : [];

  return (
    <RequestDetailLayout
      title={t("requests.user.detailTitle")}
      description={t("notary.requestDetail.idLabel", { id: er.id.slice(0, 8) })}
      onBack={onBack}
      actions={
        canCancel && !isLimitedScope ? (
          <Button
            type="button"
            variant="destructive"
            className="transition-colors duration-150"
            onClick={() => setCancelOpen(true)}
          >
            <Trash2 className="mr-2 h-4 w-4" />
            {t("requests.user.cancelRequest")}
          </Button>
        ) : undefined
      }
    >
      {/* Enterprise requests keep their own main+sidebar body (1fr/380px)
          rather than the layout's equal-halves footer slots: those are
          different layouts, and forcing them together would be the visual
          redesign this refactor explicitly is not. The container and header
          are still shared. `mt-6` is dropped because the layout supplies it. */}
      <div className={`grid gap-6 ${isLimitedScope ? "" : "xl:grid-cols-[1fr_380px]"}`}>
      {/* Main column */}
      <div className="min-w-0 space-y-6">
        {/* Details card — full for initiators/full-scope, minimal for limited */}
        {isLimitedScope ? (
          <Card>
            <CardContent className="space-y-3 py-4">
              <div className="flex items-center justify-between">
                <div className="space-y-0.5">
                  <p className="text-sm font-semibold text-foreground">
                    {er.product?.displayName ?? er.productSlug}
                    {er.enterpriseName && (
                      <span className="font-normal text-muted-foreground"> — {er.enterpriseName}</span>
                    )}
                  </p>
                  <p className="text-xs text-muted-foreground">
                    {formatDisplayDate(er.createdAt)}
                  </p>
                </div>
                <StatusBadge variant={statusVariant}>
                  {t(statusLabelKey)}
                </StatusBadge>
              </div>
              {er.user && (
                <div className="rounded-md border bg-muted/30 px-3 py-2">
                  <p className="text-xs text-muted-foreground">
                    {t("requests.user.enterprise.requestedBy")}{" "}
                    <span className="font-medium text-foreground">
                      {[er.user.firstName, er.user.lastName].filter(Boolean).join(" ").trim() || er.user.email}
                    </span>
                  </p>
                </div>
              )}
            </CardContent>
          </Card>
        ) : (
          <Card>
            <CardHeader className="pb-3">
              <CardTitle className="text-base">{t("notary.requestDetail.detailsTitle")}</CardTitle>
            </CardHeader>
            <CardContent>
              <DetailRows>
                <DetailRow label={t("requests.user.requestTypeColumn")}>
                  {er.product?.displayName ?? er.productSlug}
                </DetailRow>
                <DetailRow label={t("notary.requests.status")}>
                  <StatusBadge variant={statusVariant}>
                    {t(statusLabelKey)}
                  </StatusBadge>
                </DetailRow>
                <DetailRow label={t("notary.requests.createdAt")}>
                  {formatDisplayDate(er.createdAt)}
                </DetailRow>
              </DetailRows>
            </CardContent>
          </Card>
        )}

        {er.status === "rejected" && (
          <Card className="border-destructive/20 bg-destructive/5">
            <CardContent className="flex items-start gap-3 py-5">
              <XCircle className="mt-0.5 h-5 w-5 shrink-0 text-destructive" />
              <div className="space-y-1">
                <h3 className="text-sm font-semibold text-foreground">
                  {t("requests.user.enterprise.rejectedTitle")}
                </h3>
                {rejectionReason && (
                  <p className="text-sm text-muted-foreground">{rejectionReason}</p>
                )}
              </div>
            </CardContent>
          </Card>
        )}

        {er.status === "needs_info" && (
          <Card className="border-amber-200 bg-amber-50 dark:border-amber-900/30 dark:bg-amber-950/20">
            <CardContent className="space-y-3 py-5">
              <div className="flex items-start gap-3">
                <MessageSquareWarning className="mt-0.5 h-5 w-5 shrink-0 text-amber-600 dark:text-amber-400" />
                <div className="space-y-1">
                  <h3 className="text-sm font-semibold text-foreground">
                    {t("requests.user.enterprise.replyTitle")}
                  </h3>
                  {infoRequestMessage && (
                    <p className="text-sm text-muted-foreground">{infoRequestMessage}</p>
                  )}
                </div>
              </div>
              <div className="space-y-3">
                <Textarea
                  value={replyMessage}
                  onChange={(e) => setReplyMessage(e.target.value)}
                  placeholder={t("requests.user.enterprise.replyPlaceholder")}
                  rows={3}
                  className="bg-white dark:bg-background"
                />
                {/* Attached files */}
                {replyAttachments.length > 0 && (
                  <div className="space-y-2">
                    {replyAttachments.map((att, idx) => (
                      <div key={idx} className="flex items-center gap-2 rounded-md border bg-white px-3 py-2 dark:bg-background">
                        <Paperclip className="h-3.5 w-3.5 shrink-0 text-muted-foreground" />
                        <Input
                          value={att.label}
                          onChange={(e) => {
                            const next = [...replyAttachments];
                            next[idx] = { ...att, label: e.target.value };
                            setReplyAttachments(next);
                          }}
                          placeholder={t("requests.user.enterprise.replyAttachmentLabel")}
                          className="h-7 flex-1 text-xs"
                        />
                        <span className="shrink-0 truncate text-[10px] text-muted-foreground max-w-[120px]">
                          {att.file.name}
                        </span>
                        <button
                          type="button"
                          className="shrink-0 p-1 text-muted-foreground hover:text-destructive transition-colors"
                          onClick={() => setReplyAttachments(replyAttachments.filter((_, i) => i !== idx))}
                        >
                          <Trash2 className="h-3.5 w-3.5" />
                        </button>
                      </div>
                    ))}
                  </div>
                )}
                <div className="flex items-center justify-between">
                  <label className="cursor-pointer">
                    <input
                      type="file"
                      className="hidden"
                      accept=".pdf,.jpg,.jpeg,.png,.webp"
                      onChange={(e) => {
                        const file = e.target.files?.[0];
                        if (file) {
                          setReplyAttachments([...replyAttachments, { file, label: "" }]);
                        }
                        e.target.value = "";
                      }}
                    />
                    <span className="inline-flex items-center gap-1.5 rounded-md border border-input bg-white px-3 py-1.5 text-xs font-medium text-foreground hover:bg-accent transition-colors dark:bg-background">
                      <Paperclip className="h-3.5 w-3.5" />
                      {t("requests.user.enterprise.replyAttach")}
                    </span>
                  </label>
                  <Button
                    size="sm"
                    disabled={!replyMessage.trim() || replyMutation.isPending}
                    onClick={() => replyMutation.mutate()}
                  >
                    {replyMutation.isPending ? (
                      <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                    ) : (
                      <Send className="mr-2 h-4 w-4" />
                    )}
                    {t("requests.user.enterprise.replySend")}
                  </Button>
                </div>
              </div>
            </CardContent>
          </Card>
        )}

        {/* Completed steps data — collapsible, closed by default (hidden for limited-scope participants) */}
        {!isLimitedScope && graph && state && er.payload && (
          <Collapsible defaultOpen={false}>
            <Card>
              <CollapsibleTrigger asChild>
                <CardHeader className="cursor-pointer select-none py-3 px-4 [&[data-state=open]_svg]:rotate-180">
                  <div className="flex items-center justify-between">
                    <CardTitle className="text-base">{t("completedStepsSummary.title")}</CardTitle>
                    <ChevronDown className="h-4 w-4 shrink-0 text-muted-foreground transition-transform duration-200" />
                  </div>
                </CardHeader>
              </CollapsibleTrigger>
              <CollapsibleContent>
                <CompletedStepsSummary
                  graph={graph}
                  workflowState={state}
                  payload={er.payload as Record<string, unknown>}
                  headless
                />
              </CollapsibleContent>
            </Card>
          </Collapsible>
        )}

        {/* Pricing breakdown — collapsible, closed by default (hidden for limited-scope participants) */}
        {!isLimitedScope && graph && state && er.payload && (() => {
          const pricingNode = state.completedPath
            .map((id) => graph.nodes.find((n) => n.id === id))
            .find((n) => n?.type === "pricing");
          if (!pricingNode) return null;
          const formula = (pricingNode.data as unknown as PricingNodeData).formula;
          const flat: Record<string, unknown> = {};
          for (const [, value] of Object.entries(er.payload as Record<string, unknown>)) {
            if (value && typeof value === "object" && !Array.isArray(value)) {
              Object.assign(flat, value);
            }
          }
          const result = evaluateRateTable(formula, flat);
          if ("error" in result) return null;
          return (
            <Collapsible defaultOpen={false}>
              <Card className="border-primary/20 bg-primary/5">
                <CollapsibleTrigger asChild>
                  <CardHeader className="cursor-pointer select-none py-3 px-4 [&[data-state=open]_svg]:rotate-180">
                    <div className="flex items-center justify-between">
                      <CardTitle className="text-base">
                        {t("requests.user.wizard.pricingSummaryTitle")}
                      </CardTitle>
                      <ChevronDown className="h-4 w-4 shrink-0 text-muted-foreground transition-transform duration-200" />
                    </div>
                  </CardHeader>
                </CollapsibleTrigger>
                <CollapsibleContent>
                  <CardContent className="space-y-2 pt-0">
                    <div className="space-y-1">
                      {result.breakdown.map((line, idx) => {
                        const isLast = idx === result.breakdown.length - 1;
                        return (
                          <div
                            key={idx}
                            className={`flex items-center justify-between text-sm ${
                              isLast
                                ? "font-semibold text-foreground border-t pt-1 mt-1"
                                : "text-muted-foreground"
                            }`}
                          >
                            <span>{line.label}</span>
                            <span>{line.value}</span>
                          </div>
                        );
                      })}
                    </div>
                  </CardContent>
                </CollapsibleContent>
              </Card>
            </Collapsible>
          );
        })()}

        {/* Uploaded documents */}
        {!isLimitedScope && uploadedDocuments.length > 0 && (
          <Card>
            <CardHeader className="pb-3">
              <CardTitle className="text-base">
                {t("enterpriseStaff.requests.documentsTitle")}
              </CardTitle>
            </CardHeader>
            <CardContent>
              <Table>
                <TableHeader>
                  <TableRow>
                    <TableHead>{t("enterpriseStaff.requests.docColumnStep")}</TableHead>
                    <TableHead>{t("enterpriseStaff.requests.docColumnFile")}</TableHead>
                    <TableHead className="w-[100px]">{t("requests.user.actions")}</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {uploadedDocuments.map((doc, idx) => (
                    <TableRow key={idx}>
                      <TableCell>
                        <Badge variant="secondary" className="font-normal normal-case">
                          {doc.nodeLabel}
                        </Badge>
                      </TableCell>
                      <TableCell className="text-sm text-foreground">
                        {doc.fileName}
                      </TableCell>
                      <TableCell>
                        <AuthenticatedBlobDocumentLink
                          fetchBlob={() => fetchMyEnterpriseDocumentBlob(er.id, doc.nodeId)}
                          title={doc.fileName}
                          className="inline-flex h-8 items-center rounded-md border border-input bg-background px-3 text-xs font-medium text-foreground no-underline shadow-sm hover:bg-accent hover:text-accent-foreground"
                        >
                          {t("enterpriseStaff.requests.viewDocument")}
                        </AuthenticatedBlobDocumentLink>
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </CardContent>
          </Card>
        )}

        {/* Workflow runner / stepper */}
        {er.status !== "rejected" && (
          graph && state ? (
            <GraphWorkflowRunner
              graph={graph}
              state={state}
              enterpriseRequestId={er.id}
              allPayloadValues={er.payload}
              onCompleteNode={onCompleteStep}
              onBack={onBack}
              hideBackButton
              participantInviteNodeId={isLimitedScope ? myInviteNodeId : undefined}
            />
          ) : (
            <div className="rounded-lg border p-6 text-center text-muted-foreground">
              {t("requests.user.noWorkflowData")}
            </div>
          )
        )}
      </div>

      {/* Activity timeline sidebar — hidden for limited-scope participants */}
      {!isLimitedScope && (
        <div>
          <Card>
            <CardHeader className="pb-3">
              <CardTitle className="text-base">{t("requests.user.activityHistoryTitle")}</CardTitle>
            </CardHeader>
            <CardContent>
              {eventsQuery.isLoading ? (
                <div className="flex justify-center py-4">
                  <Loader2 className="h-5 w-5 animate-spin text-muted-foreground" />
                </div>
              ) : (
                <RequestActivityList
                  events={(eventsQuery.data ?? []).map((e) => ({
                    id: e.id,
                    type: e.type,
                    createdAt: e.createdAt,
                    actorUserId: e.actorUserId,
                    data: e.data,
                  }))}
                  config={enterpriseActivityConfig(graph, { enterpriseName: er.enterpriseName, requestUserId: er.userId })}
                  emptyMessage={t("requests.user.enterprise.activityEmpty")}
                />
              )}
            </CardContent>
          </Card>
        </div>
      )}
      </div>

      <ConfirmationDialog
        open={cancelOpen}
        onOpenChange={setCancelOpen}
        title={t("requests.user.cancelDialogTitle")}
        description={t("requests.user.cancelDialogDescription")}
        confirmText={t("requests.user.cancelRequest")}
        loadingText={t("requests.user.cancelLoading")}
        cancelText={t("pages.common.cancel")}
        variant="destructive"
        isLoading={isCancelling}
        onConfirm={async () => {
          await onCancel();
        }}
      />
    </RequestDetailLayout>
  );
}

export function UserRequestDetailPage(): JSX.Element {
  const { id } = useParams<{ id: string }>();
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();
  const location = useLocation();
  // Back goes where the user came FROM when that was handed to us, not always
  // to the list. A consumer who opened this trámite from its parent RFQ's
  // legs card expects to land back on the RFQ; sending them to the grid loses
  // the job they were looking at. Same mechanism the professional detail page
  // already uses.
  const backTarget =
    typeof (location.state as { from?: unknown } | null)?.from === "string"
      ? (location.state as { from: string }).from
      : "/requests";
  const rawSource = searchParams.get("source");
  const isEnterprise = rawSource === "enterprise";
  const isQuoteRequestSource = rawSource === "quote";
  // `?history=1` opts OUT of the hand-off redirect below, so the child request's
  // "see the quotes" link can still reach the front-stage page it came from.
  const showsQuoteHistory = searchParams.get("history") === "1";
  const sourceHint = parseCertificateRequestSourceParam(rawSource);
  const { t } = useTranslation();
  const { user } = useAuth();
  const queryClient = useQueryClient();
  const [cancelOpen, setCancelOpen] = useState(false);
  const [selectedDocumentType, setSelectedDocumentType] =
    useState<UserNotarialRequestDocumentType>("otra_documentacion_notarial");
  const [deletingDocumentId, setDeletingDocumentId] = useState<string | null>(null);
  const [manageDocumentsOpen, setManageDocumentsOpen] = useState(false);
  const [resolveMissingInfoOpen, setResolveMissingInfoOpen] = useState(false);
  const [resolveMissingInfoNotes, setResolveMissingInfoNotes] = useState("");
  const [deliveryAddressSelection, setDeliveryAddressSelection] = useState<AddressSelection>(null);
  const [saveDeliveryToBook, setSaveDeliveryToBook] = useState(true);
  const [isSubmittingDelivery, setIsSubmittingDelivery] = useState(false);
  const [editingShipmentAddress, setEditingShipmentAddress] = useState(false);
  useEffect(() => {
    if (!id) return;
    setDeliveryAddressSelection(null);
    setSaveDeliveryToBook(true);
    setEditingShipmentAddress(false);
  }, [id]);

  const requestSourceRef = useRef<ConsumerCertificateRequestSource | null>(null);

  // Enterprise request query
  const enterpriseQuery = useQuery({
    queryKey: ["me", "enterprise-requests", "detail", id],
    queryFn: () => getMyEnterpriseRequest(id!),
    enabled: Boolean(id) && isEnterprise,
  });

  // Real quote_request detail query.
  const quoteQuery = useQuery({
    queryKey: ["me", "quote-requests", "detail", id],
    queryFn: () => getMyQuoteRequestDetail(id!),
    enabled: Boolean(id) && isQuoteRequestSource,
    /**
     * A paid RFQ has one job left: spawn the request that carries the actual
     * fulfilment, and this page forwards to it once childRequestId appears.
     * That spawn happens asynchronously off the payment, so a consumer who
     * lands here in the gap sees a paid request that never moves — the page
     * has no reason to refetch and nothing tells it to.
     *
     * Poll only in that gap, and stop the moment it closes.
     */
    refetchInterval: (query) => {
      const data = query.state.data;
      if (!data) return false;
      const awaitingChild =
        (data.status === "paid" || data.status === "awarded") && !data.childRequestId;
      return awaitingChild ? 3000 : false;
    },
  });

  // Bids on my own request — only fetched once the request has reached a
  // status where bids can exist, so an early-stage request (still triaging)
  // doesn't fire a request that's guaranteed to come back empty.
  const quotesQuery = useQuery({
    queryKey: ["me", "quote-requests", "quotes", id],
    queryFn: () => listMyQuoteRequestQuotes(id!),
    enabled:
      Boolean(id) &&
      isQuoteRequestSource &&
      Boolean(quoteQuery.data) &&
      QUOTE_STATUSES_WITH_POSSIBLE_BIDS.includes(quoteQuery.data?.status ?? ""),
  });

  const quoteAttachmentInputRef = useRef<HTMLInputElement>(null);
  // The detail query is keyed ["me","quote-requests","detail",id] — see the
  // useQuery above. Both of these invalidated ["me","quote-requests",id],
  // which matches NOTHING: React Query invalidates by PREFIX, and the stored
  // key's third element is "detail", not the id. So the upload succeeded, the
  // toast fired, and the list never refetched — the file was there, and the
  // page simply never asked again.
  //
  // A near-miss key is worse than a wrong one: it looks right at a glance,
  // costs nothing at runtime, and the only symptom is stale data.
  /**
   * Which attachment is being opened, and which is awaiting a delete
   * confirmation. BOTH are per-row ids rather than booleans: the table renders
   * one button per attachment, and a shared flag would spin every row at once
   * while only one of them is actually doing anything.
   */
  const [openingQuoteAttachmentId, setOpeningQuoteAttachmentId] = useState<string | null>(null);
  const [quoteAttachmentPendingDelete, setQuoteAttachmentPendingDelete] = useState<
    { id: string; name: string } | null
  >(null);

  const addQuoteAttachmentMutation = useMutation({
    mutationFn: (file: File) =>
      addQuoteRequestAttachment(id!, file, "otra_documentacion_notarial"),
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: ["me", "quote-requests", "detail", id] });
      toast.success(t("requests.user.documentUploadSuccess"));
    },
    onError: (err) =>
      toast.error(serverMessageOr(err, t("requests.user.documentUploadError"))),
  });
  const deleteQuoteAttachmentMutation = useMutation({
    mutationFn: (attachmentId: string) => deleteQuoteRequestAttachment(id!, attachmentId),
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: ["me", "quote-requests", "detail", id] });
      toast.success(t("requests.user.documentDeleteSuccess"));
    },
    onError: () => toast.error(t("requests.user.documentDeleteError")),
  });

  const [quoteToAward, setQuoteToAward] = useState<string | null>(null);
  /*
    THE AWARD-TIME DESTINATION (spec e370e36f). A request whose consumer was
    never asked for an address — one created before the wizard asked everyone,
    or one filed with the no-Uruguayan-address opt-out — reaches this page with
    nowhere to ship. The address is collected HERE rather than earlier because
    the consumer may never award at all, and demanding it from everyone who
    merely receives a physical quote is the friction the opt-out exists to
    remove.
  */
  const [awardAddressSelection, setAwardAddressSelection] =
    useState<AddressSelection>(null);
  const [awardAddressUnpriceable, setAwardAddressUnpriceable] = useState(false);
  const [isAttachingAwardAddress, setIsAttachingAwardAddress] = useState(false);
  const [quoteToReject, setQuoteToReject] = useState<string | null>(null);
  const extendQuoteMutation = useMutation({
    mutationFn: () => extendMyQuoteRequest(id!),
    onSuccess: () => {
      toast.success(t("requests.user.extendSuccess"));
      // The legs' windows, the request status and the quote list all moved
      // server-side — including quotes the sweep had expired and the
      // extension revived. Re-read rather than patching any of it by hand.
      void queryClient.invalidateQueries({ queryKey: ["me", "quote-requests", "detail", id] });
      void queryClient.invalidateQueries({ queryKey: ["me", "quote-requests", "quotes", id] });
      void queryClient.invalidateQueries({ queryKey: ["me", "certificate-requests"] });
    },
    onError: (err: unknown) => {
      // The server's refusal is the actionable part: "no lapsed quoting
      // window to extend" means the request is still open, which a generic
      // "try again" would hide behind a retry that cannot work.
      toast.error(serverMessageOr(err, t("requests.user.extendError")));
    },
  });

  const awardQuoteMutation = useMutation({
    mutationFn: (quoteId: string) => awardMyQuoteRequestQuote(id!, quoteId),
    onSuccess: () => {
      setQuoteToAward(null);
      toast.success(t("requests.user.quoteReviewAwardSuccess"));
      void queryClient.invalidateQueries({ queryKey: ["me", "quote-requests", "detail", id] });
      void queryClient.invalidateQueries({ queryKey: ["me", "quote-requests", "quotes", id] });
      void queryClient.invalidateQueries({ queryKey: ["me", "certificate-requests"] });
    },
    onError: (err: unknown) => {
      setQuoteToAward(null);
      const status =
        err !== null && typeof err === "object" && "response" in err
          ? (err as { response?: { status?: number } }).response?.status
          : undefined;
      toast.error(
        status === 409
          ? t("requests.user.quoteReviewAwardConflictError")
          : t("requests.user.quoteReviewAwardError"),
      );
      // Most conflicts (already awarded/cancelled elsewhere) surface as a
      // plain 400 from the status guard, not just the narrower 409 race — so
      // resync on any award failure rather than leaving stale 'quoting'
      // state with live award buttons over what's actually a decided request.
      void queryClient.invalidateQueries({ queryKey: ["me", "quote-requests", "detail", id] });
      void queryClient.invalidateQueries({ queryKey: ["me", "quote-requests", "quotes", id] });
    },
  });

  const rejectQuoteMutation = useMutation({
    mutationFn: (quoteId: string) => rejectMyQuoteRequestQuote(id!, quoteId),
    onSuccess: () => {
      setQuoteToReject(null);
      toast.success(t("requests.user.quoteReviewRejectSuccess"));
      void queryClient.invalidateQueries({ queryKey: ["me", "quote-requests", "detail", id] });
      void queryClient.invalidateQueries({ queryKey: ["me", "quote-requests", "quotes", id] });
    },
    onError: () => {
      setQuoteToReject(null);
      toast.error(t("requests.user.quoteReviewRejectError"));
      void queryClient.invalidateQueries({ queryKey: ["me", "quote-requests", "detail", id] });
      void queryClient.invalidateQueries({ queryKey: ["me", "quote-requests", "quotes", id] });
    },
  });

  /**
   * One answer, seen by every bidder. The consumer is moderated on the same
   * rules as the professional (spec ce646966 §3d) — a blocked answer names
   * what it tripped so it can be rewritten, rather than failing opaquely.
   */
  const answerClarificationMutation = useMutation({
    mutationFn: ({ clarificationId, answer }: { clarificationId: string; answer: string }) =>
      answerQuoteRequestClarification(id!, clarificationId, answer),
    onSuccess: () => {
      toast.success(t("requests.clarifications.answerSuccess"));
      void queryClient.invalidateQueries({ queryKey: ["me", "quote-requests", "detail", id] });
    },
    onError: (error: unknown) => {
      const kinds = contactDetailKindsFromError(error);
      toast.error(
        kinds
          ? t("requests.clarifications.answerBlockedError")
          : t("requests.clarifications.answerError"),
      );
    },
  });


  const quoteCheckoutMutation = useMutation({
    // One checkout for the whole request (spec rfq-single-payment). legId is
    // still in the signature for callers that pass it and is ignored by the
    // server, which sums every awarded leg itself.
    // NO invoice argument. The factura question moved to the payment screen,
    // and this param is documented as "omit and the server leaves the stored
    // preference alone". Passing a hardcoded false here would record "asked
    // and declined" on a checkout where nobody was asked yet — and that
    // distinction is real: null means never asked, false means declined, and
    // fiscalo-invoice.service honours both differently.
    mutationFn: (legId?: string) => createMyQuoteRequestCheckout(id!, legId),
    onSuccess: (created) => {
      toastIfCheckoutAutoPaidBypass(created);
      if (created.redirectUrl) {
        window.location.assign(created.redirectUrl);
        return;
      }
      /**
       * MERCADO PAGO HAS NO REDIRECT, so without this the consumer would land
       * back on this page with a pending checkout and nowhere to pay it. The
       * card is entered on our own page, inside Mercado Pago's iframes.
       */
      if (created.paymentMethod === "mercadopago" && created.status !== "paid") {
        window.location.assign(
          `/pay/mercadopago?checkout_id=${encodeURIComponent(created.checkoutId)}`,
        );
        return;
      }
      /**
       * A checkout the consumer already switched to transfer. Without this they
       * could no longer pay at all: the server resumes the pending row, the
       * card branch above does not match it, and the request sat on "Esperando
       * pago" with a button that did nothing.
       */
      if (created.paymentMethod === "bank_transfer" && created.status !== "paid") {
        window.location.assign(
          `/pay/bank-transfer?checkout_id=${encodeURIComponent(created.checkoutId)}`,
        );
        return;
      }
      void queryClient.invalidateQueries({ queryKey: ["me", "quote-requests", "detail", id] });
      void queryClient.invalidateQueries({ queryKey: ["me", "certificate-requests"] });
    },
    onError: (err) => {
      // The server's own message, when it has one. Every refusal on this path
      // is a sentence the consumer can act on — "a discipline is still
      // collecting bids", "the currencies differ", "a checkout already
      // exists" — and the generic fallback hid all of them behind "intentá de
      // nuevo", which is advice that does not work for any of those causes.
      toast.error(serverMessageOr(err, t("requests.user.quoteReviewCheckoutError")));
    },
  });

  // Consumer (notarial/accountant) request query
  const { data: loaded, isLoading: consumerLoading, isError: consumerError } = useQuery({
    queryKey: ["me", "certificate-requests", "detail", id, sourceHint ?? "auto"],
    queryFn: () =>
      getMyConsumerCertificateRequestDetail(id!, { source: sourceHint ?? undefined }),
    enabled: Boolean(id) && !isEnterprise && !isQuoteRequestSource,
  });

  const isLoading = isEnterprise
    ? enterpriseQuery.isLoading
    : isQuoteRequestSource
      ? quoteQuery.isLoading
      : consumerLoading;
  const isError = isEnterprise
    ? enterpriseQuery.isError
    : isQuoteRequestSource
      ? quoteQuery.isError
      : consumerError;
  const request = loaded?.detail;
  const requestSource = loaded?.source;
  const certificateLabelContext: NotarialCertificateLabelContext =
    requestSource === "accountant" ? "accountant" : "notary";

  // Asking only makes sense once something has actually been delivered
  // digitally, and the presence of the certificate document IS that condition:
  // a physically-delivered request has no such row, and no upload could fix it
  // anyway. The server refuses the rest; this only decides whether to offer
  // the button.
  const canRequestCorrection =
    request?.status === "completed" &&
    !request.correctionOpen &&
    request.documents.some((d) => d.documentType === "notarial_certificate");

  useEffect(() => {
    requestSourceRef.current = requestSource ?? null;
  }, [requestSource]);

  const addressListStatuses =
    request &&
    (request.status === "document_ready_at_professional" ||
      request.status === "document_at_platform" ||
      request.status === "awaiting_consumer_delivery_details" ||
      request.status === "awaiting_shipment_to_consumer" ||
      request.status === "in_transit_to_consumer");

  const { data: addressesData } = useQuery({
    queryKey: ["me", "addresses"],
    queryFn: listUserAddresses,
    enabled: Boolean(id && user && addressListStatuses),
  });
  const isIncomeCertificateRequest = request?.requestType === "income_certificate";
  const cancelMutation = useMutation({
    mutationFn: async () => {
      if (!id) throw new Error("Missing request id");
      const src = requestSourceRef.current;
      if (src === "accountant") {
        await cancelMyAccountantRequest(id);
        return;
      }
      await cancelMyNotarialRequest(id);
    },
    onSuccess: () => {
      toast.success(t("requests.user.cancelSuccess"));
      invalidateConsumerCertificateCaches(queryClient, id!);
      navigate("/requests", { replace: true });
    },
    onError: () => {
      toast.error(t("requests.user.cancelError"));
    },
  });
  // Bulk state for the manage-documents dialog. There is no single-file
  // counterpart any more: the slot upload path takes its File as an
  // argument to handleUploadIntoSlot rather than reading it from state.
  const [selectedFiles, setSelectedFiles] = useState<File[]>([]);
  const [documentNote, setDocumentNote] = useState("");

  const uploadDocumentsBulkMutation = useMutation({
    mutationFn: (payload: {
      requestId: string;
      documentType: UserNotarialRequestDocumentType;
      files: File[];
      note: string | null;
    }) => {
      const src = requestSourceRef.current;
      const upload =
        src === "accountant" ? uploadAccountantDocumentsBulk : uploadNotarialDocumentsBulk;
      return upload(payload.requestId, payload.documentType, payload.files, payload.note);
    },
    onSuccess: (result) => {
      // Partial success is a normal outcome here, not an exception: the server
      // stores what it can and names what it could not. Reporting only the
      // success would repeat the failure this feature exists to fix — files
      // silently not arriving.
      if (result.failed.length > 0) {
        toast.error(
          t("requests.user.documentsUploadPartial", {
            count: result.failed.length,
            files: result.failed.map((f) => f.filename).join(", "),
          }),
        );
      } else {
        toast.success(t("requests.user.documentsUploadSuccess", { count: result.stored.length }));
      }
      setSelectedFiles([]);
      setDocumentNote("");
      setManageDocumentsOpen(false);
      if (id) invalidateConsumerCertificateCaches(queryClient, id);
    },
    onError: (err) => {
      toast.error(serverMessageOr(err, t("requests.user.documentUploadError")));
    },
  });

  // What the professional asked for. Its own query: an upload closes one slot
  // without changing anything on the request itself, so this list moves on a
  // different schedule than the detail and the detail is heavy to refetch.
  const documentSlotsQueryKey = [
    "me",
    "certificate-requests",
    id,
    "document-slots",
    requestSource ?? "auto",
  ];
  const documentSlotsQuery = useQuery({
    queryKey: documentSlotsQueryKey,
    queryFn: () =>
      requestSource === "accountant"
        ? getMyAccountantRequestDocumentSlots(id!)
        : getMyNotarialRequestDocumentSlots(id!),
    enabled: Boolean(id) && !isEnterprise && !isQuoteRequestSource && Boolean(requestSource),
  });
  const documentSlots: RequestDocumentSlot[] = documentSlotsQuery.data?.documents ?? [];

  const [uploadingSlotId, setUploadingSlotId] = useState<string | null>(null);
  const uploadSlotDocumentMutation = useMutation({
    mutationFn: (payload: { requestId: string; slotId: string; file: File }) => {
      const src = requestSourceRef.current;
      if (src === "accountant") {
        return uploadMyAccountantRequestDocumentForSlot(
          payload.requestId,
          payload.slotId,
          payload.file,
        );
      }
      return uploadMyNotarialRequestDocumentForSlot(
        payload.requestId,
        payload.slotId,
        payload.file,
      );
    },
    onSuccess: () => {
      toast.success(t("requests.user.documentUploadSuccess"));
      if (id) invalidateConsumerCertificateCaches(queryClient, id);
      // Invalidated, not refetched through the query object: this key has
      // other observers (the professional's view of the same request opens it
      // too) and refetch() would only refresh this one.
      void queryClient.invalidateQueries({ queryKey: documentSlotsQueryKey });
    },
    onError: (err) => {
      toast.error(serverMessageOr(err, t("requests.user.documentUploadError")));
    },
    onSettled: () => {
      setUploadingSlotId(null);
    },
  });

  const handleUploadIntoSlot = (slotId: string, file: File | null): void => {
    if (!id || !file) return;
    setUploadingSlotId(slotId);
    uploadSlotDocumentMutation.mutate({ requestId: id, slotId, file });
  };

  useEffect(() => {
    if (!user || !request || request.id !== id) return;
    const resolved = request.fulfillmentMode ?? "physical_only";
    const allowsPhysical =
      resolved === "physical_only" || resolved === "digital_and_physical";
    const showDelivery =
      allowsPhysical &&
      (request.status === "document_ready_at_professional" ||
        request.status === "document_at_platform" ||
        request.status === "awaiting_consumer_delivery_details");
    if (!showDelivery) return;
    const suggested = defaultDeliveryRecipientDisplayName(user);
    if (!suggested) return;
    setDeliveryAddressSelection((prev) => {
      if (prev?.kind === "new") {
        if (prev.payload.contactPersonName?.trim()) return prev;
        return {
          ...prev,
          payload: { ...prev.payload, contactPersonName: suggested },
        };
      }
      if (prev?.kind === "saved") {
        if (prev.contactPersonName?.trim()) return prev;
        return { ...prev, contactPersonName: suggested };
      }
      return prev;
    });
  }, [id, user, request?.id, request?.status, request?.fulfillmentMode, deliveryAddressSelection]);

  useEffect(() => {
    if (!request?.deliveryAddressId) return;
    const fromRequest = request.deliveryNotes?.trim() ?? "";
    setDeliveryAddressSelection((prev) => {
      if (prev?.kind !== "saved" || prev.addressId !== request.deliveryAddressId) return prev;
      if ((prev.courierNotes ?? "").trim() === fromRequest) return prev;
      return { ...prev, courierNotes: fromRequest || undefined };
    });
  }, [request?.deliveryAddressId, request?.deliveryNotes, request?.id]);

  useEffect(() => {
    if (request?.status === "in_transit_to_consumer") {
      setEditingShipmentAddress(false);
    }
  }, [request?.status]);

  useEffect(() => {
    if (!editingShipmentAddress || !request) return;
    if (request.deliveryAddressId) {
      setDeliveryAddressSelection({
        kind: "saved",
        addressId: request.deliveryAddressId,
        contactPhone: request.deliveryContactPhone ?? undefined,
        contactPersonName: request.deliveryContactName ?? undefined,
        courierNotes: request.deliveryNotes ?? undefined,
      });
    } else {
      setDeliveryAddressSelection(null);
    }
  }, [
    editingShipmentAddress,
    request?.id,
    request?.deliveryAddressId,
    request?.deliveryContactPhone,
    request?.deliveryContactName,
    request?.deliveryNotes,
  ]);

  const resubmitMutation = useMutation({
    mutationFn: (payload: { requestId: string; notes?: string }) => {
      const src = requestSourceRef.current;
      if (src === "accountant") {
        return resubmitMyAccountantRequest(payload.requestId, payload.notes);
      }
      return resubmitMyNotarialRequest(payload.requestId, payload.notes);
    },
    onSuccess: () => {
      toast.success(
        t(
          isIncomeCertificateRequest
            ? "requests.user.missingInfoResolveSuccessAccountant"
            : "requests.user.missingInfoResolveSuccessNotary",
        ),
      );
      setResolveMissingInfoOpen(false);
      setResolveMissingInfoNotes("");
      if (id) invalidateConsumerCertificateCaches(queryClient, id);
    },
    onError: () => {
      toast.error(t("requests.user.missingInfoResolveError"));
    },
  });
  const submitDelivery = useCallback(async (): Promise<void> => {
    if (!id || !isAddressSelectionReady(deliveryAddressSelection, { minContactPhoneLength: 6 })) return;
    setIsSubmittingDelivery(true);
    try {
      let deliveryAddressId: string | undefined;
      let newDeliveryAddress: UserAddressCreatePayload | undefined;
      if (deliveryAddressSelection?.kind === "saved") {
        deliveryAddressId = deliveryAddressSelection.addressId;
      } else if (deliveryAddressSelection?.kind === "new") {
        if (saveDeliveryToBook) {
          const created = await createUserAddress(deliveryAddressSelection.payload);
          deliveryAddressId = created.id;
          void queryClient.invalidateQueries({ queryKey: ["me", "addresses"] });
        } else {
          newDeliveryAddress = deliveryAddressSelection.payload;
        }
      }
      const deliveryContactPhone =
        deliveryAddressSelection?.kind === "saved"
          ? deliveryAddressSelection.contactPhone?.trim() || undefined
          : deliveryAddressSelection?.kind === "new"
            ? deliveryAddressSelection.payload.contactPhone?.trim() || undefined
            : undefined;
      const deliveryContactName =
        deliveryAddressSelection?.kind === "saved"
          ? deliveryAddressSelection.contactPersonName?.trim() || undefined
          : deliveryAddressSelection?.kind === "new"
            ? deliveryAddressSelection.payload.contactPersonName?.trim() || undefined
            : undefined;
      const deliveryNotes =
        deliveryAddressSelection?.kind === "saved"
          ? (deliveryAddressSelection.courierNotes ?? "").trim()
          : deliveryAddressSelection?.kind === "new"
            ? (deliveryAddressSelection.payload.courierNotes ?? "").trim()
            : "";
      const deliveryBody = {
        deliveryAddressId,
        newDeliveryAddress,
        deliveryContactPhone,
        deliveryContactName,
        deliveryNotes,
      };
      if (requestSourceRef.current === "accountant") {
        await submitMyAccountantRequestDeliveryDetails(id, deliveryBody);
      } else {
        await submitMyNotarialRequestDeliveryDetails(id, deliveryBody);
      }
      toast.success(t("requests.user.deliveryDetailsSuccess"));
      setDeliveryAddressSelection(null);
      setSaveDeliveryToBook(true);
      setEditingShipmentAddress(false);
      invalidateConsumerCertificateCaches(queryClient, id);
      void queryClient.invalidateQueries({ queryKey: ["me", "addresses"] });
    } catch {
      toast.error(t("requests.user.deliveryDetailsError"));
    } finally {
      setIsSubmittingDelivery(false);
    }
  }, [id, deliveryAddressSelection, saveDeliveryToBook, queryClient, t]);

  const deleteDocumentMutation = useMutation({
    mutationFn: (payload: { requestId: string; documentId: string }) => {
      const src = requestSourceRef.current;
      if (src === "accountant") {
        return deleteMyAccountantRequestDocument(payload.requestId, payload.documentId);
      }
      return deleteMyNotarialRequestDocument(payload.requestId, payload.documentId);
    },
    onSuccess: () => {
      toast.success(t("requests.user.documentDeleteSuccess"));
      setDeletingDocumentId(null);
      if (id) invalidateConsumerCertificateCaches(queryClient, id);
    },
    onError: () => {
      toast.error(t("requests.user.documentDeleteError"));
      setDeletingDocumentId(null);
    },
  });

  useEffect(() => {
    if (isError) {
      toast.error(t("requests.user.error"));
      navigate("/requests", { replace: true });
    }
  }, [isError, navigate, t]);

  const enterpriseCancelMutation = useMutation({
    mutationFn: async () => {
      if (!id) throw new Error("Missing request id");
      await cancelMyEnterpriseRequest(id);
    },
    onSuccess: () => {
      toast.success(t("requests.user.cancelSuccess"));
      void queryClient.invalidateQueries({ queryKey: ["me", "enterprise-requests"] });
      navigate("/requests", { replace: true });
    },
    onError: () => {
      toast.error(t("requests.user.cancelError"));
    },
  });

  const quoteCancelMutation = useMutation({
    mutationFn: async () => {
      if (!id) throw new Error("Missing request id");
      await cancelMyQuoteRequest(id);
    },
    onSuccess: () => {
      toast.success(t("requests.user.cancelSuccess"));
      void queryClient.invalidateQueries({ queryKey: ["me", "certificate-requests"] });
      navigate("/requests", { replace: true });
    },
    onError: () => {
      toast.error(t("requests.user.cancelError"));
    },
  });

  if (!id) {
    navigate("/requests", { replace: true });
    return <></>;
  }

  if (isLoading) {
    return (
      <LoadingPage
        title={t("requests.user.detail.loadingTitle", "Cargando tu solicitud")}
        description={t(
          "requests.user.detail.loadingDescription",
          "Estamos trayendo los detalles y los documentos asociados. Puede tomar unos segundos.",
        )}
      />
    );
  }

  // Real quote_request detail, including the review/award screen once bids exist.
  // Hand-off: an RFQ owns only the front stage. Once payment has spawned the
  // child request, that child IS the request -- it runs the normal fulfillment
  // lifecycle with a timeline, documents and an assigned professional -- so the
  // consumer lands there instead of on the sparse quote summary. `replace` keeps
  // Back going to /requests rather than bouncing through the redirect again.
  if (isQuoteRequestSource && quoteQuery.data?.childRequestId && !showsQuoteHistory) {
    const landedSource =
      quoteQuery.data.childRequestType === "accountant_request" ? "accountant" : "notarial";
    return (
      <Navigate to={`/requests/${quoteQuery.data.childRequestId}?source=${landedSource}`} replace />
    );
  }

  if (isQuoteRequestSource && quoteQuery.data) {
    const q = quoteQuery.data;
    const quoteAttachments = q.attachments ?? [];
    const openQuoteAttachment = async (attachmentId: string): Promise<void> => {
      // The document is fetched authenticated, buffered and then opened, so
      // there is a real wait with nothing on screen — and on a large file the
      // button simply looked dead. Tracked per attachment id so only the row
      // being opened shows it.
      setOpeningQuoteAttachmentId(attachmentId);
      try {
        await openAuthenticatedBlobInNewTab(() =>
          downloadQuoteRequestAttachment(q.id, attachmentId),
        );
      } catch {
        toast.error(t("requests.user.quoteAttachmentOpenError"));
      } finally {
        setOpeningQuoteAttachmentId(null);
      }
    };
    // Attachments stay editable for exactly as long as the RFQ stays
    // cancellable -- the same pre-award window the backend enforces. Once
    // awarded, documents belong to the spawned child request.
    // Mirrors the catalog detail's "Pago" row: the amount plus a badge for
    // whether it cleared. Falls back to the awarded quote's price when a
    // checkout has not been created yet, so an awarded-but-unpaid RFQ still
    // shows what was agreed.
    // The fallback used to be the awarded quote's PRICE, which contradicted the
    // charge summary directly below it on the same screen: "Pago $ 6.547,00"
    // over "Total $ 7.075,20". q.charge is the same preview the checkout
    // charges from, so an awarded-but-unpaid RFQ now names the amount actually
    // owed. The price is kept as a last resort for a charge the server could
    // not compute (no awarded leg, mixed currencies).
    const quotePaymentAmount = q.payment ?? (q.charge
      ? {
          amountMinor: q.charge.totalMinor,
          currency: q.charge.currency,
          status: "pending",
          paidAt: null,
        }
      : q.award
      ? { amountMinor: q.award.priceMinor, currency: q.award.currency, status: "pending", paidAt: null }
      : null);
    const quotePaymentRow = quotePaymentAmount && (q.legs ?? []).length <= 1 ? (
      <DetailRow label={t("requests.user.paymentColumnHeader")}>
        <span className="flex flex-wrap items-center gap-2">
          {formatUyMinor(quotePaymentAmount.amountMinor, quotePaymentAmount.currency)}
          <StatusBadge variant={quotePaymentAmount.status === "paid" ? "success" : "warning"}>
            {t(
              quotePaymentAmount.status === "paid"
                ? "requests.user.paymentStatusPaid"
                : "requests.user.paymentStatusPending",
            )}
          </StatusBadge>
          {/**
           * THE ACTION BELONGS WHERE THE STATUS IS. Awarding a quote closes a
           * dialog and drops the consumer back on this page, where the only
           * way to pay was a button far below the bids, the invoice options
           * and a fold — so the request read "Esperando pago" with nothing
           * visibly to do about it, and people did not find it.
           *
           * The button at the bottom stays: it sits with the totals being
           * agreed to, which is the right place to confirm an amount. This one
           * is for the consumer who came back to a request that is waiting on
           * them.
           */}
          {quotePaymentAmount.status !== "paid" && q.charge ? (
            <Button
              type="button"
              size="sm"
              onClick={() => quoteCheckoutMutation.mutate(undefined)}
              disabled={quoteCheckoutMutation.isPending}
            >
              {t("requests.user.quoteReviewPayButton")}
            </Button>
          ) : null}
        </span>
      </DetailRow>
    ) : null;

    // A request needing two professionals cannot be described by one award,
    // one amount or one status, so the per-leg card replaces those rows
    // entirely rather than sitting alongside them.
    const quoteLegs = q.legs ?? [];
    const quoteHasMultipleLegs = quoteLegs.length > 1;

    const canEditQuoteAttachments = isQuoteRequestCancellable(q.status);
    // Same pre-award window: once a quote is awarded the auction is over, and
    // an answer would reach bidders who can no longer act on it.
    const isQuoteOpenForClarifications = isQuoteRequestCancellable(q.status);
    const quoteActivityConfig = quoteRequestActivityConfig();

    // Per-leg broadcasts are one dispatch to the consumer, not two events.
    // Collapsed BEFORE mapping so the helper sees `legId` and can name the
    // disciplines; `data` is how the shared list carries them to the label.
    const quoteLegDisciplines = new Map(quoteLegs.map((leg) => [leg.id, leg.discipline]));
    const quoteActivityEvents = collapseQuoteBroadcastEvents(
      q.events ?? [],
      quoteLegDisciplines,
    ).map((e) => ({
      id: e.id,
      type: e.eventType,
      createdAt: e.createdAt,
      actorDisplayName: e.actorDisplayName,
      actorRole: e.actorRole,
      message: e.message,
      data: e.broadcastDisciplines ? { broadcastDisciplines: e.broadcastDisciplines } : null,
    }));

    // Footer columns are passed to RequestDetailLayout as slots; the layout
    // owns the two-column grid so it cannot drift from the other surfaces.
    const quoteAttachmentsCard = (
      <ReferenceSection
        label={t("requests.user.quoteAttachmentsTitle")}
        action={
          canEditQuoteAttachments ? (
            <Button
              type="button"
              variant="outline"
              size="sm"
              className="transition-colors duration-150"
              onClick={() => quoteAttachmentInputRef.current?.click()}
              disabled={addQuoteAttachmentMutation.isPending}
            >
              {/* A spinner, not just a disabled button. Disabled alone says
                  "you cannot press this", which on a button the user HAS just
                  pressed reads as the click being ignored — and an upload of
                  any size takes long enough to doubt it. The label changes too,
                  because a greyed button with unchanged text is the state most
                  easily mistaken for nothing happening. */}
              {addQuoteAttachmentMutation.isPending ? (
                <Loader2 className="mr-2 h-4 w-4 animate-spin motion-reduce:animate-none" />
              ) : (
                <Upload className="mr-2 h-4 w-4" />
              )}
              {t(
                addQuoteAttachmentMutation.isPending
                  ? "requests.user.addDocumentPending"
                  : "requests.user.addDocument",
              )}
            </Button>
          ) : undefined
        }
      >
        <>
          <input
            ref={quoteAttachmentInputRef}
            type="file"
            className="hidden"
            onChange={(e) => {
              const file = e.target.files?.[0];
              // Reset first: picking the same filename twice must still fire.
              e.target.value = "";
              if (file) addQuoteAttachmentMutation.mutate(file);
            }}
          />
          {/* Shared with the professional's view of this same request, so the
              two cannot drift again. Delete is passed only while the RFQ is
              still editable; the professional's side passes no onDelete at all.
              It ASKS FIRST — deleting went straight through on a single click,
              with no undo anywhere, and the file goes from storage as well as
              from the row. */}
          <QuoteAttachmentsTable
            attachments={quoteAttachments}
            certificateLabelContext={certificateLabelContext}
            onOpen={(attachmentId) => void openQuoteAttachment(attachmentId)}
            openingAttachmentId={openingQuoteAttachmentId}
            onDelete={
              canEditQuoteAttachments
                ? (attachment) =>
                    setQuoteAttachmentPendingDelete({
                      id: attachment.id,
                      name: attachment.originalFilename,
                    })
                : undefined
            }
            isDeleting={deleteQuoteAttachmentMutation.isPending}
            /* The message turns on whether they can still act. Inviting an
               upload when the header's button is gone is an instruction the
               screen cannot carry out, so the closed case states the fact and
               stops. */
            empty={
              <EmptyState
                className="py-8"
                title={t("requests.user.quoteAttachmentsEmptyTitle")}
                message={t(
                  canEditQuoteAttachments
                    ? "requests.user.quoteAttachmentsEmptyMessage"
                    : "requests.user.quoteAttachmentsEmptyClosed",
                )}
              />
            }
          />

          {/* Mounted beside the table rather than per row: one dialog driven by
              which attachment is pending, so N rows do not mount N dialogs. */}
          <ConfirmationDialog
            open={quoteAttachmentPendingDelete != null}
            onOpenChange={(open) => {
              if (!open) setQuoteAttachmentPendingDelete(null);
            }}
            title={t("requests.user.quoteAttachmentDeleteTitle")}
            description={t("requests.user.quoteAttachmentDeleteDescription", {
              name: quoteAttachmentPendingDelete?.name ?? "",
            })}
            confirmText={t("requests.user.quoteAttachmentDeleteConfirm")}
            cancelText={t("pages.common.cancel")}
            variant="destructive"
            isLoading={deleteQuoteAttachmentMutation.isPending}
            onConfirm={() => {
              const pending = quoteAttachmentPendingDelete;
              if (!pending) return;
              deleteQuoteAttachmentMutation.mutate(pending.id, {
                // Cleared on settle, not on click: closing it early would hide
                // the dialog's own loading state and make a slow delete look
                // like it did nothing.
                onSettled: () => setQuoteAttachmentPendingDelete(null),
              });
            }}
          />
        </>
      </ReferenceSection>
    );

    const quoteActivityCard = (
      <ReferenceSection label={t("requests.user.activityHistoryTitle")}>
        <RequestActivityList
          events={quoteActivityEvents}
          config={quoteActivityConfig}
          emptyMessage={t("requests.user.activityEmpty")}
        />
      </ReferenceSection>
    );
    const isQuoteCancellable = isQuoteRequestCancellable(q.status);
    // Deliberately NOT gated on quotingClosed: awarding stays legal through the
    // whole grace period after bidding shuts. That gap is the point of the
    // grace period — a consumer deciding between bids must not lose the
    // ability to accept one the moment the window closes.
    const canAwardQuotes = q.status === "quoting";
    // Bidding, however, really has stopped: the bidding endpoint gates on this
    // same timestamp, so the badge and the empty state must stop promising
    // quotes that can no longer arrive.
    const quotingClosed = isQuotingClosed(
      q.status as Parameters<typeof isQuotingClosed>[0],
      quoteLegs,
    );
    const showQuotesSection = QUOTE_STATUSES_WITH_POSSIBLE_BIDS.includes(q.status);
    const quotes = quotesQuery.data ?? [];
    const quotePendingAward = quotes.find((quote) => quote.id === quoteToAward) ?? null;
    // Built above the JSX rather than inline: the repo's rule is that props are
    // references. The description is now one lead sentence — the figures and
    // the payment window are QuoteAwardSummary's job, because a table shows
    // three amounts better than a sentence containing them does.
    const awardConfirmDescription = quotePendingAward
      ? t("requests.user.quoteReviewAwardConfirmLead", {
          name: resolveQuoteProfessionalName(quotePendingAward, t),
        })
      : "";
    // Only the OTHER bids on the same leg count: a sibling leg's quotes are a
    // separate decision and are not released by this award.
    /*
      THE SERVER DECIDES THIS, not the page. `needsDeliveryAddress` is computed
      by the same predicate awardQuote refuses with, so the dialog and the API
      cannot disagree — a second copy of the rule here is how a confirm button
      ends up enabled for an award that is about to be rejected.
    */
    const awardNeedsAddress = quotePendingAward?.needsDeliveryAddress === true;

    const attachAddressThenAward = async (quoteId: string): Promise<void> => {
      const selection = awardAddressSelection;
      if (!selection) return;
      setIsAttachingAwardAddress(true);
      try {
        /*
          ATTACH FIRST, AWARD SECOND, and never the other way round. Awarding
          first would leave a won quote on a request with nowhere to ship if
          the attach then failed; this ordering can only fail the harmless way,
          leaving an address on a request nobody awarded yet.
        */
        const addressId =
          selection.kind === "saved"
            ? selection.addressId
            : (await createUserAddress(selection.payload)).id;
        await setMyQuoteRequestDeliveryAddress(id!, addressId);
        awardQuoteMutation.mutate(quoteId);
      } catch (err) {
        toast.error(serverMessageOr(err, t("requests.user.quoteDeliverySaveError")));
      } finally {
        setIsAttachingAwardAddress(false);
      }
    };

    const awardConfirmBody = awardNeedsAddress ? (
      <QuoteDeliveryAddressStep
        selection={awardAddressSelection}
        onSelectionChange={setAwardAddressSelection}
        disabled={isAttachingAwardAddress || awardQuoteMutation.isPending}
        onPriceabilityChange={setAwardAddressUnpriceable}
      />
    ) : quotePendingAward ? (
      <QuoteAwardSummary
        priceMinor={quotePendingAward.priceMinor}
        consumerFeeMinor={quotePendingAward.consumerFeeMinor}
        deliveryFeeMinor={quotePendingAward.deliveryFeeMinor}
        totalMinor={quotePendingAward.totalMinor}
        currency={quotePendingAward.currency}
        pricesIncludeIva={quotePendingAward.pricesIncludeIva}
        hasOtherQuotes={quotes.some(
          (quote) =>
            quote.id !== quotePendingAward.id &&
            quote.legId === quotePendingAward.legId &&
            quote.status === "submitted",
        )}
      />
    ) : null;

    /*
      FROM THE LEGS, NOT FROM THE QUOTES.

      This used to be `[...new Set(quotes.map(q => q.legId))]`, which means a
      leg nobody has bid on produces no group at all. On a two-discipline
      request where only the contador has answered, the escribano leg was not
      shown as empty — it was ABSENT, and the section read as "these are all
      the quotes" when it meant "these are the contador's". The consumer had
      no way to see the other discipline exists here, is still collecting, or
      has closed.

      Driving it off `quoteLegs` also fixes the order: the legs' own order is
      stable, where the old one depended on which professional happened to bid
      first.

      THE FALLBACK IS LOAD-BEARING. A request whose legs did not load would
      otherwise render NO bids whatsoever — a worse failure than the one being
      fixed — so an empty `quoteLegs` keeps the old quotes-derived grouping.
    */
    const disciplineByLegId = new Map(quoteLegs.map((leg) => [leg.id, leg.discipline]));
    const quotesByLeg =
      quoteLegs.length > 0
        ? quoteLegs.map((leg) => ({
            legId: leg.id,
            discipline: leg.discipline,
            leg,
            items: quotes.filter((quote) => quote.legId === leg.id),
          }))
        : [...new Set(quotes.map((q) => q.legId))].map((legId) => ({
            legId,
            discipline: disciplineByLegId.get(legId) ?? null,
            leg: null,
            items: quotes.filter((q) => q.legId === legId),
          }));

    const quoteReviewCard = showQuotesSection ? (
      <Card>
        <CardHeader className="pb-3">
          <CardTitle className="text-base">
            {t("requests.user.quoteReviewSectionTitle")}
          </CardTitle>
        </CardHeader>
        <CardContent className="space-y-4">
          {quotesQuery.isLoading ? (
            <div className="flex justify-center py-6">
              <Loader2 className="h-5 w-5 animate-spin text-muted-foreground" />
            </div>
          ) : quotesQuery.isError ? (
            <EmptyState
              title={t("requests.user.quoteReviewErrorTitle")}
              message={t("requests.user.quoteReviewErrorMessage")}
            />
          ) : quotes.length === 0 ? (
            <EmptyState
              title={t(
                quotingClosed
                  ? "requests.user.quoteReviewClosedTitle"
                  : "requests.user.quoteReviewEmptyTitle",
              )}
              message={t(
                quotingClosed
                  ? "requests.user.quoteReviewClosedMessage"
                  : "requests.user.quoteReviewEmptyMessage",
              )}
              /* The closed message says "podés volver a publicar la solicitud
                 cuando quieras" — which was a promise with nothing behind it.
                 The action belongs where the sentence offering it is. */
              action={
                quotingClosed ? (
                  <Button
                    type="button"
                    variant="outline"
                    disabled={extendQuoteMutation.isPending}
                    onClick={() => extendQuoteMutation.mutate()}
                  >
                    <CalendarPlus className="mr-2 h-4 w-4" />
                    {t("requests.user.extendRequest")}
                  </Button>
                ) : undefined
              }
            />
          ) : (
            // Grouped by leg. On a two-discipline request this list holds
            // BOTH legs' bids, and ungrouped an escribano's price sits beside
            // a contador's as if the consumer were choosing between them —
            // they are two separate jobs, each awarded separately.
            quotesByLeg.map(({ legId, discipline, leg, items }) => {
              /*
                PER LEG, NOT PER REQUEST. `canAwardQuotes` above is
                `q.status === "quoting"`, and a two-discipline RFQ's parent
                stays 'quoting' until EVERY leg resolves — so an already
                awarded leg inherited "you may still choose" and went on
                offering "Elegir esta cotización" on the bids it had just
                beaten. The consumer saw one bid marked "Elegida · pendiente
                de pago" and the one beneath it still asking to be picked.

                Once a leg is awarded the others are history, not options —
                apps/mobile-users has computed it this way all along
                (quote-bids-section.tsx).
              */
              const legIsDecided = items.some((quote) => quote.status === "awarded");
              const canAwardThisLeg = canAwardQuotes && !legIsDecided;
              return (
              /*
                A CONTAINED SECTION, not a run of rows under a faint label.
                Both headings were 12px muted text at identical weight, the
                first group's rows were boxed and the second's empty state was
                not, so two separate jobs read as one continuous list.
              */
              <div
                key={legId}
                className={
                  quoteHasMultipleLegs
                    ? "space-y-3 rounded-lg border border-border bg-muted/20 p-3"
                    : "space-y-3"
                }
              >
                {/* How many, and in what order. The server returns these
                    cheapest-first within a currency, and a list of offers that
                    does not say it is sorted invites the consumer to assume it
                    is not. */}
                <div className="flex flex-wrap items-center justify-between gap-2">
                  {quoteHasMultipleLegs && discipline ? (
                    <div className="flex flex-wrap items-center gap-2">
                      <p className="text-sm font-medium text-foreground">
                        {t("requests.user.quoteReviewLegHeading", {
                          discipline: t(`requests.user.quoteRequestDiscipline_${discipline}`),
                        })}
                      </p>
                      {/*
                        THE STATE OF THIS DISCIPLINE, said at the heading.
                        "What is going on" is a per-leg question, and it was
                        only answerable by reading a badge on the first ROW —
                        which a leg with no bids does not have at all. Same
                        badge the legs card shows for the same leg, through the
                        shared helpers, so the two cannot disagree.
                      */}
                      {leg != null ? (
                        <StatusBadge
                          variant={
                            isLegQuotingClosed(leg)
                              ? "warning"
                              : legStatusBadgeVariant(leg.status)
                          }
                        >
                          {isLegQuotingClosed(leg)
                            ? t("requests.user.quoteLegStatus_quoting_closed")
                            : t(`requests.user.quoteLegStatus_${leg.status}`, {
                                defaultValue: leg.status,
                              })}
                        </StatusBadge>
                      ) : null}
                    </div>
                  ) : (
                    <span />
                  )}
                  {/* The sort note is an AUCTION affordance — it tells a
                      consumer how to read a list they are choosing from. Above
                      a decided leg it invites a comparison that is over. */}
                  {items.length > 1 && !legIsDecided ? (
                    <p className="text-xs text-muted-foreground">
                      {t("requests.user.quoteReviewSortedByPrice", { count: items.length })}
                    </p>
                  ) : null}
                </div>
                {/*
                  WHEN BIDS STOP ARRIVING, said on the page and not only in the
                  mail. `quotingExpiresAt` reaches this page on every leg and
                  was read only to colour a badge, so a consumer looking at one
                  quote could not tell whether it was the whole field or the
                  first of several — and the only thing that ever named the
                  bidding window was the nudge that fires as it shuts.

                  Under the heading rather than beside the sort note: both are
                  muted 12px, and the two of them on one line read as a single
                  sentence about sorting.
                */}
                {leg != null && isLegQuotingOpen(leg) && !legIsDecided ? (
                  <p className="text-xs text-muted-foreground">
                    {t("requests.user.quoteMoreQuotesUntil", {
                      date: formatDisplayDate(leg.quotingExpiresAt ?? ""),
                    })}
                  </p>
                ) : null}
                {/* Labels the two figure columns. At one bid there is no
                    column to read down, so the amount and the day count are
                    otherwise two bare numbers. */}
                {/*
                  A LEG NOBODY HAS BID ON, said out loud. Only on a
                  multi-discipline request: with one leg the card's own empty
                  state already covers it and carries the Extend action, so a
                  second one here would state it twice.

                  It reads the LEG's clock, not the request's — one leg can be
                  shut while its sibling still collects, which is the whole
                  reason the request-level answer cannot speak for a row.
                */}
                {items.length === 0 ? (
                  /*
                    SMALLER THAN A DECIDED LEG, deliberately. At the shared
                    EmptyState's full weight a discipline nobody has bid on
                    outweighed the one already chosen directly above it, which
                    inverts which of the two the consumer should be reading.
                    It is a note inside its section, not the card's subject.
                  */
                  <EmptyState
                    className="gap-1 py-4"
                    title={t(
                      leg != null && isLegQuotingClosed(leg)
                        ? "requests.user.quoteReviewLegEmptyClosedTitle"
                        : "requests.user.quoteReviewLegEmptyTitle",
                    )}
                    message={t(
                      leg != null && isLegQuotingClosed(leg)
                        ? "requests.user.quoteReviewLegEmptyClosedMessage"
                        : "requests.user.quoteReviewLegEmptyMessage",
                    )}
                  />
                ) : (
                  <>
                    <QuoteReviewColumnHeader />
                    {items.map((quote) => (
                  <QuoteReviewCard
                    key={quote.id}
                    quote={quote}
                    canAward={canAwardThisLeg}
                    isAwarding={awardQuoteMutation.isPending}
                    onAward={setQuoteToAward}
                    onReject={setQuoteToReject}
                    awardPaid={q.payment?.status === "paid"}
                  />
                    ))}
                  </>
                )}
              </div>
              );
            })
          )}
        </CardContent>
      </Card>
    ) : null;

    const quoteQuestionsCard =
      (q.clarifications ?? []).length > 0 ? (
        <QuoteClarificationsCard
          clarifications={q.clarifications ?? []}
          viewer="consumer"
          canCompose={isQuoteOpenForClarifications}
          onAnswer={(clarificationId, answer) =>
            answerClarificationMutation.mutate({ clarificationId, answer })
          }
          answeringId={
            answerClarificationMutation.isPending
              ? answerClarificationMutation.variables?.clarificationId ?? null
              : null
          }
        />
      ) : null;
    // Estimated delivery, computed from the LEGS rather than the parent's
    // award — the parent's is null on a two-discipline request by design
    // (27c505ed), which is why the old ETA row simply vanished there.
    //
    // The estimates are SUMMED, not maxed. The disciplines are sequential
    // steps of one trámite, not parallel tracks: the intake's own scope
    // rationale for this case reads "con el contador como paso principal",
    // because the company has to exist before the BCU declaration can be
    // made. A contador at 2 days and an escribano at 5 is 7 days of work, not
    // 5. Maxing them was the parallel-work assumption and it under-promises.
    //
    // Summing is also the safer error: where two disciplines genuinely could
    // overlap, the estimate is merely conservative, whereas a max on
    // sequential work commits to a date that cannot be met.
    //
    // Dated from PAYMENT rather than from today: a professional starts when
    // they are paid, so a date derived from now would quietly slide forward
    // every day the consumer waited. Unpaid, it says "N days from payment",
    // which is the only true thing available.
    const awardedEtaLegs = (q.legs ?? []).filter((leg) => leg.award?.estimatedDays != null);
    const totalEtaDays =
      awardedEtaLegs.length > 0
        ? awardedEtaLegs.reduce((sum, leg) => sum + (leg.award?.estimatedDays ?? 0), 0)
        : q.award?.estimatedDays ?? null;
    const paidAtIso =
      (q.legs ?? []).find((leg) => leg.payment?.paidAt != null)?.payment?.paidAt ?? null;
    let estimatedDeliveryLabel: string | null = null;
    if (totalEtaDays != null) {
      if (paidAtIso != null) {
        const due = new Date(paidAtIso);
        due.setDate(due.getDate() + totalEtaDays);
        estimatedDeliveryLabel = formatDisplayDate(due.toISOString());
      } else {
        estimatedDeliveryLabel = t("requests.user.quoteEtaDaysFromPayment", {
          count: totalEtaDays,
        });
      }
    }

    const childRequestSource = q.childRequestType === "accountant_request" ? "accountant" : "notarial";
    return (
      <RequestDetailLayout
        title={q.title}
        onBack={() => navigate(backTarget)}
        actions={
          <>
            {/* The constructive option first, and only once the window has
                actually shut. On a live request there is nothing to extend
                and the server refuses it. */}
            {quotingClosed ? (
              <Button
                type="button"
                variant="outline"
                className="transition-colors duration-150"
                disabled={extendQuoteMutation.isPending}
                onClick={() => extendQuoteMutation.mutate()}
              >
                <CalendarPlus className="mr-2 h-4 w-4" />
                {t("requests.user.extendRequest")}
              </Button>
            ) : null}
            {isQuoteCancellable ? (
              <Button
                type="button"
                variant="destructive"
                className="transition-colors duration-150"
                onClick={() => setCancelOpen(true)}
              >
                <Trash2 className="mr-2 h-4 w-4" />
                {t("requests.user.cancelRequest")}
              </Button>
            ) : null}
          </>
        }
        primary={quoteAttachmentsCard}
        secondary={quoteActivityCard}
        overlays={
          <>
            <ConfirmationDialog
              open={cancelOpen}
              onOpenChange={setCancelOpen}
              title={t("requests.user.cancelDialogTitle")}
              description={t("requests.user.cancelDialogDescription")}
              confirmText={t("requests.user.cancelRequest")}
              cancelText={t("pages.common.cancel")}
              variant="destructive"
              isLoading={quoteCancelMutation.isPending}
              onConfirm={() => quoteCancelMutation.mutateAsync()}
            />
            <ConfirmationDialog
              open={quoteToAward != null}
              onOpenChange={(open) => {
                if (!open) {
                  setQuoteToAward(null);
                  setAwardAddressSelection(null);
                  setAwardAddressUnpriceable(false);
                }
              }}
              title={
                awardNeedsAddress
                  ? t("requests.user.quoteAwardAddressTitle")
                  : t("requests.user.quoteReviewAwardConfirmTitle")
              }
              description={
                awardNeedsAddress
                  ? t("requests.user.quoteAwardAddressDescription")
                  : awardConfirmDescription
              }
              body={awardConfirmBody}
              confirmText={
                awardNeedsAddress
                  ? t("requests.user.quoteAwardAddressConfirm")
                  : t("requests.user.quoteReviewAwardButton")
              }
              cancelText={t("pages.common.cancel")}
              /* Disabled rather than hidden: a confirm button that vanishes
                 reads as the dialog being broken. The blocker names the gap. */
              confirmDisabled={
                awardNeedsAddress &&
                (!isAddressSelectionReady(awardAddressSelection) ||
                  awardAddressUnpriceable)
              }
              isLoading={awardQuoteMutation.isPending || isAttachingAwardAddress}
              onConfirm={() => {
                if (!quoteToAward) return;
                if (!awardNeedsAddress) {
                  awardQuoteMutation.mutate(quoteToAward);
                  return;
                }
                void attachAddressThenAward(quoteToAward);
              }}
            />
            <ConfirmationDialog
              open={quoteToReject != null}
              onOpenChange={(open) => {
                if (!open) setQuoteToReject(null);
              }}
              title={t("requests.user.quoteReviewRejectConfirmTitle")}
              description={t("requests.user.quoteReviewRejectConfirmDescription")}
              confirmText={t("requests.user.quoteReviewRejectButton")}
              cancelText={t("pages.common.cancel")}
              variant="destructive"
              isLoading={rejectQuoteMutation.isPending}
              onConfirm={() => {
                if (quoteToReject) rejectQuoteMutation.mutate(quoteToReject);
              }}
            />
          </>
        }
      >
        <Card>
          <CardHeader className="pb-3">
            <CardTitle className="text-base">
              {t("requests.user.quoteRequestSummaryTitle")}
            </CardTitle>
          </CardHeader>
          <CardContent>
            <DetailRows>
              <DetailRow label={t("requests.user.quoteRequestFieldStatus")}>
                <StatusBadge
                  variant={getQuoteConsumerStatusBadgeVariant(
                    q.status as Parameters<typeof getQuoteConsumerStatusBadgeVariant>[0],
                    quoteLegs,
                  )}
                >
                  {t(
                    getQuoteConsumerStatusTranslationKey(
                      q.status as Parameters<typeof getQuoteConsumerStatusTranslationKey>[0],
                      quoteLegs,
                    ),
                  )}
                </StatusBadge>
              </DetailRow>
              {/* The PARENT's discipline stays 'undecided' on a
                  two-discipline request by design (spec 27c505ed), so this row
                  reads "Sin definir todavía" on a request whose legs card
                  names both professions. The legs card is the honest view. */}
              {quoteHasMultipleLegs ? null : (
                <DetailRow label={t("requests.user.quoteRequestFieldDiscipline")}>
                  {t(`requests.user.quoteRequestDiscipline_${q.discipline}`)}
                </DetailRow>
              )}
              <DetailRow label={t("notary.requests.createdAt")}>
                {formatDisplayDate(q.createdAt)}
              </DetailRow>
              <DetailRow label={t("requests.user.quoteRequestFieldDescription")}>
                {q.description}
              </DetailRow>
              {/* Award + payment rows appear only once they exist. Pre-award an
                  RFQ genuinely has no price, professional or delivery mode, so
                  rendering empty rows would be noise rather than parity. */}
              {/* Name, not award: the server withholds the professional's
                  identity until the checkout clears, so between award and
                  payment this row would read "Profesional — Un profesional",
                  which is the empty-row noise the rule above rejects. The
                  consumer still sees "Profesional 1" on the bid itself. */}
              {q.award?.professionalDisplayName ? (
                <DetailRow label={t("requests.user.quoteAwardedProfessional")}>
                  {q.award.professionalDisplayName}
                </DetailRow>
              ) : null}
              {estimatedDeliveryLabel != null ? (
                <DetailRow label={t("requests.user.quoteEstimatedDelivery")}>
                  {estimatedDeliveryLabel}
                </DetailRow>
              ) : null}
              {quotePaymentRow}
            </DetailRows>
          </CardContent>
        </Card>
        {/* Who is doing what, and what each half costs — that belongs with
            the summary of the request, not below the bids the consumer is
            still comparing. */}
        {quoteHasMultipleLegs ? (
          <QuoteRequestLegsCard
            quoteRequestId={q.id}
            legs={quoteLegs}
            charge={q.charge ?? null}
            onPay={() => quoteCheckoutMutation.mutate(undefined)}
            isPaying={quoteCheckoutMutation.isPending}
          />
        ) : null}
        {/* Side by side: the two things the consumer acts on while an RFQ is
            open are the bids and the questions, and stacking them full-width
            pushed the questions below the fold on a request with several
            quotes. Paired only when BOTH exist — one alone takes the full
            width rather than leaving half the row empty. */}
        {quoteReviewCard && quoteQuestionsCard ? (
          <div className="grid gap-4 lg:grid-cols-2 lg:items-start">
            {quoteReviewCard}
            {quoteQuestionsCard}
          </div>
        ) : (
          <>
            {quoteReviewCard}
            {quoteQuestionsCard}
          </>
        )}
        {/* A single-discipline request pays through the same summary as a
            multi-discipline one. It used to be a bare Pagar button with no
            amount beside it, which was survivable while the charge was the
            bid the consumer had just read — and stops being survivable the
            moment the platform adds a service fee to it. */}
        {quoteHasMultipleLegs ? null : q.status === "awarded" && q.charge != null ? (
          <QuoteChargeSummary
            charge={q.charge}
            totalLabelKey="requests.user.quoteLegsTotal"
            canPay
            onPay={() => quoteCheckoutMutation.mutate(undefined)}
            isPaying={quoteCheckoutMutation.isPending}
          />
        ) : null}
        {q.childRequestId && (
          <Card>
            <CardHeader className="pb-3">
              <CardTitle className="text-base">
                {t("requests.user.quoteReviewChildLinkTitle")}
              </CardTitle>
            </CardHeader>
            <CardContent className="flex items-center justify-between gap-4">
              <p className="text-sm text-muted-foreground">
                {t("requests.user.quoteReviewChildLinkDescription")}
              </p>
              <Button
                onClick={() => navigate(`/requests/${q.childRequestId}?source=${childRequestSource}`)}
              >
                {t("requests.user.quoteReviewChildLinkButton")}
              </Button>
            </CardContent>
          </Card>
        )}
      </RequestDetailLayout>
    );
  }

  // Enterprise request detail view
  if (isEnterprise && enterpriseQuery.data) {
    const er = enterpriseQuery.data;
    const graph = er.graph ?? null;
    const state = er.workflowState as GraphState | null;
    const handleCompleteStep = async (nodeId: string, data: Record<string, unknown>) => {
      // Check for file uploads (__file_* keys contain File objects)
      const fileEntries = Object.entries(data).filter(
        ([key, val]) => key.startsWith("__file_") && val instanceof File,
      );
      if (fileEntries.length > 0) {
        if (fileEntries.length === 1) {
          // Single file — use the combined upload+complete endpoint
          await uploadEnterpriseRequestDocument(er.id, nodeId, fileEntries[0][1] as File);
        } else {
          // Multiple files — upload all to blob storage first, then complete once
          const uploadedUrls: Record<string, string> = {};
          for (const [key, file] of fileEntries) {
            const fieldKey = key.replace("__file_", "");
            const result = await uploadEnterpriseRequestBlob(er.id, file as File);
            uploadedUrls[fieldKey] = result.fileUrl;
          }
          await completeMyEnterpriseRequestStep(er.id, nodeId, uploadedUrls);
        }
        await enterpriseQuery.refetch();
        void queryClient.invalidateQueries({ queryKey: ["me", "enterprise-requests", "events", er.id] });
        return;
      }
      await completeMyEnterpriseRequestStep(er.id, nodeId, data);
      await enterpriseQuery.refetch();
      void queryClient.invalidateQueries({ queryKey: ["me", "enterprise-requests", "events", er.id] });
    };

    const ENTERPRISE_STATUS_VARIANT: Record<string, StatusBadgeVariant> = {
      pending: "warning",
      in_review: "info",
      needs_info: "danger",
      awaiting_action: "info",
      approved: "success",
      rejected: "softDanger",
      cancelled: "softDanger",
      completed: "success",
      awaiting_child: "info",
    };
    const erStatusVariant: StatusBadgeVariant =
      ENTERPRISE_STATUS_VARIANT[er.status] ?? "info";
    const erStatusLabelKey = `requests.user.enterprise.status${er.status
      .split("_")
      .map((p: string) => p.charAt(0).toUpperCase() + p.slice(1))
      .join("")}`;

    return (
      <EnterpriseRequestDetailView
        er={er}
        graph={graph}
        state={state}
        statusVariant={erStatusVariant}
        statusLabelKey={erStatusLabelKey}
        onCompleteStep={handleCompleteStep}
        onBack={() => navigate(backTarget)}
        onCancel={() => enterpriseCancelMutation.mutateAsync()}
        isCancelling={enterpriseCancelMutation.isPending}
        t={t}
      />
    );
  }

  if (!request) {
    return (
      <LoadingPage
        title={t("requests.user.detail.loadingTitle", "Cargando tu solicitud")}
        description={t(
          "requests.user.detail.loadingDescription",
          "Estamos trayendo los detalles y los documentos asociados. Puede tomar unos segundos.",
        )}
      />
    );
  }

  const bookAddressForDelivery: UserAddressDto | null =
    request.deliveryAddressId && addressesData?.items
      ? (addressesData.items.find((a) => a.id === request.deliveryAddressId) ?? null)
      : null;
  const deliverySummary = buildDeliverySummaryProps(request, bookAddressForDelivery);
  const fulfillmentResolvedConsumer = request.fulfillmentMode ?? "physical_only";
  const consumerAllowsPhysical =
    fulfillmentResolvedConsumer === "physical_only" ||
    fulfillmentResolvedConsumer === "digital_and_physical";
  const showShipmentStatusCard =
    consumerAllowsPhysical &&
    (request.status === "awaiting_shipment_to_consumer" || request.status === "in_transit_to_consumer");
  const canEditDeliveryBeforeShipment = request.status === "awaiting_shipment_to_consumer";

  const consumerVisibleEvents = filterConsumerVisibleActivityEvents(request.events);
  const consumerStatusLabelKey = getConsumerStatusTranslationKey(request.status);
  const consumerStatusVariant = getConsumerStatusBadgeVariant(request.status);
  const consumerFulfillmentHintKey = getConsumerFulfillmentHintTranslationKey(request.status);
  const showConsumerDeliveryForm =
    consumerAllowsPhysical &&
    (request.status === "document_ready_at_professional" ||
      request.status === "document_at_platform" ||
      request.status === "awaiting_consumer_delivery_details");
  const hasSubmittedDeliveryDetails = Boolean(request.deliveryAddressSnapshot?.trim());

  const canManageDocuments =
    request.status === "pending" ||
    request.status === "missing_information" ||
    request.status === "in_progress";
  /* Was a hardcoded ternary here until spec 18c82ff1 §5f; the professional's
     required-document picker needs the same mapping, and a third copy is how
     the three drift apart. The list and the fallback are unchanged. */
  const availableDocumentTypes: readonly UserNotarialRequestDocumentType[] =
    documentTypesForRequestType(request.requestType);
  const protectedDocumentTypes = getProtectedDocumentTypesForRequestType(request.requestType);
  const professionalHasAccepted = request.acceptedByUserId != null;

  const canConsumerCancelRequest =
    request.status === "pending" || request.status === "cannot_complete";


  // Slots for RequestDetailLayout. The layout owns the container, header,
  // banner placement and the two-column footer grid, so this page describes
  // WHAT goes in each region and never re-derives the shell.
  const profileGateBanner = (
    <>
    {/* Profile-gated banner: the request is parked until the requester
        finishes their profile (avatar + verified phone). Deep-links into
        the same /complete-profile page the wizard's success handler uses,
        so the user lands on the detail again once the gate clears. */}
    {request.status === "pending_profile_completion" && (
      <button
        type="button"
        onClick={() =>
          navigate(
            `/complete-profile?next=${encodeURIComponent(
              `/requests/${id}${requestSource ? `?source=${requestSource}` : ""}`,
            )}`,
          )
        }
        className="mt-6 flex w-full items-center gap-3 rounded-lg border border-warning/40 bg-warning/10 p-4 text-left transition hover:bg-warning/15"
      >
        <AlertTriangle className="h-5 w-5 shrink-0 text-warning" aria-hidden />
        <div className="min-w-0 flex-1">
          <p className="text-sm font-semibold text-foreground">
            {t(
              "requests.user.profileGatedBannerTitle",
              "Tu solicitud está en pausa",
            )}
          </p>
          <p className="text-xs text-muted-foreground">
            {t(
              "requests.user.profileGatedBannerBody",
              "Completá tu perfil (foto + teléfono) para que un profesional pueda tomarla.",
            )}
          </p>
        </div>
        <ChevronRight className="h-5 w-5 shrink-0 text-muted-foreground" aria-hidden />
      </button>
    )}
    </>
  );

  // Split by WHO SENT IT, not by documentType. `notarial_certificate` was the
  // only direction signal the data used to carry, and it cannot tell a file
  // the professional sent from one the consumer uploaded.
  const ownDocuments = request.documents.filter(
    (d) => d.uploaderRole === "consumer" && d.documentType !== "notarial_certificate",
  );
  const professionalDocuments = request.documents.filter(
    (d) => d.uploaderRole === "professional" && d.documentType !== "notarial_certificate",
  );

  /**
   * One documents table.
   *
   * `deletable` is false for everything the professional sent: the consumer
   * may read and download those, never remove them. The server refuses it too
   * (deleteConsumerDocument checks uploaderRole) -- this only keeps the UI
   * from offering an action that would be rejected.
   */
  const renderDocumentsTable = (
    docs: UserNotarialRequestDocument[],
    deletable: boolean,
  ): JSX.Element => (
    <Table>
      <TableHeader>
        <TableRow>
          <TableHead>{t("notary.requests.type")}</TableHead>
          <TableHead>{t("notary.requestDetail.documentName")}</TableHead>
          <TableHead>{t("notary.requestDetail.documentDate")}</TableHead>
          {deletable ? (
            <TableHead className="w-[120px]">{t("requests.user.actions")}</TableHead>
          ) : null}
        </TableRow>
      </TableHeader>
      <TableBody>
        {docs.map((doc) => (
          <TableRow key={doc.id}>
            <TableCell>
              <Badge variant="secondary" className="font-normal normal-case">
                {t(
                  notarialDocumentTypeTranslationKey(
                    doc.documentType,
                    certificateLabelContext,
                  ),
                  doc.documentType,
                )}
              </Badge>
            </TableCell>
            <TableCell>
              <AuthenticatedBlobDocumentLink
                fetchBlob={() =>
                  requestSource === "accountant"
                    ? fetchMyAccountantRequestDocumentBlob(id!, doc.id)
                    : fetchMyNotarialRequestDocumentBlob(id!, doc.id)
                }
                title={doc.originalFilename}
                className="hover:no-underline"
              >
                {doc.originalFilename}
              </AuthenticatedBlobDocumentLink>
              {/* Under the filename rather than its own column: a note is
                  shared across a batch, so a column would repeat the same
                  sentence on every row of it. */}
              {doc.note ? (
                <p className="mt-1 max-w-prose text-xs text-muted-foreground">
                  {doc.note}
                </p>
              ) : null}
            </TableCell>
            <TableCell className="text-sm text-muted-foreground">
              {formatDisplayDate(doc.createdAt)}
            </TableCell>
            {deletable ? (
              <TableCell>
                {canManageDocuments ? (
                  protectedDocumentTypes.has(
                    doc.documentType as UserNotarialRequestDocumentType,
                  ) ? (
                    <span className="text-xs text-muted-foreground">
                      {t("requests.user.requiredDocument")}
                    </span>
                  ) : professionalHasAccepted &&
                    doc.documentType !== "otra_documentacion_notarial" ? (
                    <span className="text-xs text-muted-foreground">—</span>
                  ) : (
                    <Button
                      type="button"
                      variant="outline"
                      size="sm"
                      onClick={() => {
                        setDeletingDocumentId(doc.id);
                        deleteDocumentMutation.mutate({
                          requestId: id,
                          documentId: doc.id,
                        });
                      }}
                      disabled={
                        deleteDocumentMutation.isPending ||
                        deletingDocumentId === doc.id
                      }
                    >
                      {deletingDocumentId === doc.id ? (
                        <>
                          <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                          {t("requests.user.deletingDocument")}
                        </>
                      ) : (
                        <>
                          <Trash2 className="mr-2 h-4 w-4" />
                          {t("common.delete")}
                        </>
                      )}
                    </Button>
                  )
                ) : (
                  <span className="text-xs text-muted-foreground">—</span>
                )}
              </TableCell>
            ) : null}
          </TableRow>
        ))}
      </TableBody>
    </Table>
  );

  const documentsCard = (
    <ReferenceSection
      label={t("notary.requests.documents")}
      action={
        canManageDocuments ? (
          <Button
            type="button"
            variant="outline"
            size="sm"
            onClick={() => setManageDocumentsOpen(true)}
            className="transition-colors duration-150"
          >
            <Upload className="mr-2 h-4 w-4" />
            {t("requests.user.addDocument")}
          </Button>
        ) : undefined
      }
    >
      <>
        {/* What was asked for comes FIRST: it is the only part of this card
            that needs an action, and it is why the consumer opened the page. */}
        <DocumentSlotsList
          slots={documentSlots}
          labelContext={certificateLabelContext}
          onUpload={handleUploadIntoSlot}
          uploadingSlotId={uploadingSlotId}
          // Read-only outside the window the server accepts uploads in: a
          // button that would be refused is worse than no button.
          disabled={!canManageDocuments}
        />
        {ownDocuments.length === 0 && professionalDocuments.length === 0 ? (
          <p className="text-sm text-muted-foreground">{t("notary.requestDetail.noDocuments")}</p>
        ) : (
          <div className="space-y-5">
            {/* Grouped by who sent it. Before uploaderRole existed the only
                way to tell was `documentType !== "notarial_certificate"`,
                which cannot distinguish a file the professional sent from one
                the consumer uploaded -- so an attachment would have landed in
                this consumer's OWN list, with a delete button on it. */}
            {professionalDocuments.length > 0 ? (
              <section className="space-y-2">
                <h4 className="text-xs font-medium uppercase tracking-wide text-muted-foreground">
                  {t("requests.user.documentsFromProfessional")}
                </h4>
                {renderDocumentsTable(professionalDocuments, false)}
              </section>
            ) : null}
            <section className="space-y-2">
              {/* The heading only earns its place once there are two groups to
                  tell apart; a single-group card is just "your documents". */}
              {professionalDocuments.length > 0 ? (
                <h4 className="text-xs font-medium uppercase tracking-wide text-muted-foreground">
                  {t("requests.user.documentsFromYou")}
                </h4>
              ) : null}
              {ownDocuments.length > 0 ? (
                renderDocumentsTable(ownDocuments, true)
              ) : (
                <p className="text-sm text-muted-foreground">
                  {t("notary.requestDetail.noDocuments")}
                </p>
              )}
            </section>
          </div>
        )}
      </>
    </ReferenceSection>

  );

  const activityCard = (
    <ReferenceSection label={t("requests.user.activityHistoryTitle")}>
      <RequestActivityList
          events={consumerVisibleEvents.map((e) => ({
            id: e.id,
            type: e.eventType,
            createdAt: e.createdAt,
            actorDisplayName: e.actorDisplayName,
            actorRole: e.actorRole,
            message: e.message,
          }))}
        config={notarialActivityConfig(undefined, certificateLabelContext)}
        emptyMessage={t("requests.user.activityHistoryEmpty")}
      />
    </ReferenceSection>
  );

  const detailDialogs = (
    <>
    <Dialog open={manageDocumentsOpen} onOpenChange={setManageDocumentsOpen}>
      <DialogContent>
        <DialogHeader>
          <DialogTitle>{t("requests.user.manageDocumentsTitle")}</DialogTitle>
        </DialogHeader>
        <div className="space-y-3">
          <p className="text-xs text-muted-foreground">{t("requests.user.manageDocumentsHint")}</p>
          <div className="space-y-2">
            <label className="text-sm font-medium text-foreground" htmlFor="document-type-select">
              {t("notary.requests.type")}
            </label>
            <Select
              value={selectedDocumentType}
              onValueChange={(value) =>
                setSelectedDocumentType(value as UserNotarialRequestDocumentType)
              }
              >
              <SelectTrigger id="document-type-select" className="w-full">
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                {availableDocumentTypes.map((documentType) => (
                  <SelectItem key={documentType} value={documentType}>
                    {t(
                      notarialDocumentTypeTranslationKey(
                        documentType,
                        certificateLabelContext,
                      ),
                      documentType,
                    )}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>
          <StyledMultiFileInput
            id="request-document-upload"
            accept=".pdf,.jpg,.jpeg,.png,.webp"
            value={selectedFiles}
            onChange={setSelectedFiles}
            disabled={uploadDocumentsBulkMutation.isPending}
            label={t("requests.user.uploadDocumentFileLabel")}
            hint="PDF, JPG, PNG, WebP"
            maxFiles={MAX_FILES_PER_BULK_UPLOAD}
          />

          {/* One note for the batch. Tipo is already chosen once above, so the
              note follows the same shape rather than asking per file. */}
          <div className="space-y-1.5">
            <label
              className="text-sm font-medium text-muted-foreground"
              htmlFor="request-document-note"
            >
              {t("requests.user.documentNoteLabel")}
            </label>
            <Textarea
              id="request-document-note"
              value={documentNote}
              onChange={(e) => setDocumentNote(e.target.value)}
              disabled={uploadDocumentsBulkMutation.isPending}
              placeholder={t("requests.user.documentNotePlaceholder")}
              maxLength={2000}
              rows={3}
            />
          </div>

          <div className="flex justify-end">
            <Button
              type="button"
              onClick={() => {
                if (!id || selectedFiles.length === 0) return;
                uploadDocumentsBulkMutation.mutate({
                  requestId: id,
                  documentType: selectedDocumentType,
                  files: selectedFiles,
                  note: documentNote.trim() || null,
                });
              }}
              disabled={selectedFiles.length === 0 || uploadDocumentsBulkMutation.isPending}
            >
              {uploadDocumentsBulkMutation.isPending ? (
                <>
                  <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                  {t("requests.user.uploadingDocument")}
                </>
              ) : (
                <>
                  <Upload className="mr-2 h-4 w-4" />
                  {t("requests.user.uploadDocument")}
                </>
              )}
            </Button>
          </div>
        </div>
      </DialogContent>
    </Dialog>
    <Dialog
      open={resolveMissingInfoOpen}
      onOpenChange={(open) => {
        setResolveMissingInfoOpen(open);
        if (!open) {
          setResolveMissingInfoNotes("");
        }
      }}
    >
      <DialogContent className="sm:max-w-md motion-reduce:transition-none">
        <DialogHeader>
          <DialogTitle className="text-foreground">{t("requests.user.missingInfoResolveDialogTitle")}</DialogTitle>
          <DialogDescription>
            {t(
              isIncomeCertificateRequest
                ? "requests.user.missingInfoResolveDialogDescriptionAccountant"
                : "requests.user.missingInfoResolveDialogDescriptionNotary",
            )}
          </DialogDescription>
        </DialogHeader>
        <div className="space-y-2">
          <Label htmlFor="resolve-missing-info-notes" className="text-foreground">
            {t("requests.user.missingInfoResolveNotesLabel")}
          </Label>
          <Textarea
            id="resolve-missing-info-notes"
            value={resolveMissingInfoNotes}
            onChange={(e) => setResolveMissingInfoNotes(e.target.value)}
            placeholder={t(
              isIncomeCertificateRequest
                ? "requests.user.missingInfoResolveNotesPlaceholderAccountant"
                : "requests.user.missingInfoResolveNotesPlaceholderNotary",
            )}
            rows={4}
            disabled={resubmitMutation.isPending}
            className="min-h-[100px] resize-y transition-colors duration-150"
          />
        </div>
        <DialogFooter className="flex w-full flex-row justify-between gap-2 sm:justify-between">
          <Button
            type="button"
            variant="outline"
            className="transition-colors duration-150"
            onClick={() => setResolveMissingInfoOpen(false)}
            disabled={resubmitMutation.isPending}
          >
            {t("requests.user.missingInfoResolveCancel")}
          </Button>
          <Button
            type="button"
            className="transition-colors duration-150"
            disabled={resubmitMutation.isPending || !id}
            onClick={() => {
              if (!id) return;
              void resubmitMutation.mutateAsync({
                requestId: id,
                notes: resolveMissingInfoNotes.trim() || undefined,
              });
            }}
          >
            {resubmitMutation.isPending ? (
              <>
                <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                {t("requests.user.missingInfoResolveSubmitting")}
              </>
            ) : (
              t("requests.user.missingInfoResolveConfirm")
            )}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
    <ConfirmationDialog
      open={cancelOpen}
      onOpenChange={setCancelOpen}
      title={t("requests.user.cancelDialogTitle")}
      description={
        request.status === "cannot_complete"
          ? t("requests.user.cancelDialogDescriptionCannotComplete")
          : t("requests.user.cancelDialogDescription")
      }
      confirmText={t("requests.user.cancelRequest")}
      loadingText={t("requests.user.cancelLoading")}
      cancelText={t("pages.common.cancel")}
      variant="destructive"
      isLoading={cancelMutation.isPending}
      onConfirm={() => {
        if (!id) return;
        return cancelMutation.mutateAsync();
      }}
    />
    </>
  );

  return (
    <RequestDetailLayout
      title={t("requests.user.detailTitle")}
      description={t("notary.requestDetail.idLabel", { id: id.slice(0, 8) })}
      onBack={() => navigate(backTarget)}
      actions={
        canConsumerCancelRequest ? (
          <Button
            type="button"
            variant="destructive"
            className="transition-colors duration-150"
            onClick={() => setCancelOpen(true)}
          >
            <Trash2 className="mr-2 h-4 w-4" />
            {t("requests.user.cancelRequest")}
          </Button>
        ) : undefined
      }
      banner={profileGateBanner}
      primary={documentsCard}
      secondary={activityCard}
      overlays={detailDialogs}
    >


      {isIncomeCertificateRequest ? (
        <IncomeCertificateSummaryCard details={request.incomeCertificateDetails} />
      ) : null}

      <Card>
        <CardHeader className="pb-3">
          <CardTitle className="text-base">{t("notary.requestDetail.detailsTitle")}</CardTitle>
        </CardHeader>
        <CardContent>
          <DetailRows>
            <DetailRow label={t("requests.user.requestTypeColumn")}>
              {formatCertificatePricingProductWithSource(
                request.requestType,
                request.sourceRequestTitle,
                t,
              )}
            </DetailRow>
            {request.sourceRequestId ? (
              <DetailRow label={t("requests.user.quoteSourceLinkLabel")} valueClassName="text-sm">
                <Button
                  type="button"
                  variant="link"
                  className="h-auto p-0 text-sm"
                  onClick={() =>
                    navigate(`/requests/${request.sourceRequestId}?source=quote&history=1`)
                  }
                >
                  {t("requests.user.quoteSourceLinkAction")}
                </Button>
              </DetailRow>
            ) : null}
            <DetailRow label={t("notary.requests.status")}>
              <StatusBadge variant={consumerStatusVariant}>
                {t(consumerStatusLabelKey)}
              </StatusBadge>
            </DetailRow>
            <DetailRow label={t("notary.requests.createdAt")}>
              {formatDisplayDate(request.createdAt)}
            </DetailRow>
            <DetailRow label={t("requests.user.fulfillmentColumnHeader")}>
              {fulfillmentResolvedConsumer === "digital_only"
                ? t("requests.user.fulfillmentDigitalOnly")
                : fulfillmentResolvedConsumer === "digital_and_physical"
                  ? t("requests.user.fulfillmentDigitalAndPhysical")
                  : t("requests.user.fulfillmentPhysicalOnly")}
            </DetailRow>
            {request.estimatedCompletionDate && (
              <DetailRow label={t("requests.user.estimatedDateColumnHeader")}>
                {formatDisplayDate(request.estimatedCompletionDate)}
              </DetailRow>
            )}
            {request.payment ? (
              <DetailRow label={t("requests.user.paymentColumnHeader")} valueClassName="flex flex-wrap items-center gap-2 text-sm text-foreground">
                <span>{formatUyMinor(request.payment.amountMinor, request.payment.currency)}</span>
                <StatusBadge variant={request.payment.status === "paid" ? "success" : "warning"}>
                  {request.payment.status === "paid"
                    ? t("requests.user.paymentStatusPaid")
                    : t("requests.user.paymentStatusPending")}
                </StatusBadge>
              </DetailRow>
            ) : null}
            {request.certificateNotes && (
              <DetailRow label={t("notarial.notarialCertificateNotaryNotesLabel")} valueClassName="text-sm text-foreground whitespace-pre-wrap">
                {request.certificateNotes}
              </DetailRow>
            )}
            {request.certificateValidityDate ? (() => {
              const validityMs = new Date(request.certificateValidityDate).getTime();
              const nowMs = Date.now();
              const daysLeft = Math.ceil((validityMs - nowMs) / (1000 * 60 * 60 * 24));
              const isExpired = daysLeft < 0;
              const isExpiringSoon = !isExpired && daysLeft <= 30;
              const badgeVariant = isExpired ? "danger" : isExpiringSoon ? "warning" : "success";
              const badgeLabel = isExpired
                ? t("requests.user.validityExpired")
                : isExpiringSoon
                  ? t("requests.user.validityExpiringSoon")
                  : t("requests.user.validityValid");
              return (
                <DetailRow label={t("requests.user.validityColumnHeader")} valueClassName="flex items-center gap-2">
                  <span className="text-sm text-foreground">{formatDisplayDate(request.certificateValidityDate)}</span>
                  <StatusBadge variant={badgeVariant}>{badgeLabel}</StatusBadge>
                </DetailRow>
              );
            })() : null}
          </DetailRows>

          {/* Estimated completion progress bar */}
          {request.estimatedCompletionDate && request.acceptedAt &&
            request.status !== "completed" && request.status !== "cannot_complete" ? (() => {
              const acceptedMs = new Date(request.acceptedAt).getTime();
              const estimatedMs = new Date(request.estimatedCompletionDate).getTime();
              const nowMs = Date.now();
              const totalDuration = estimatedMs - acceptedMs;
              const elapsed = nowMs - acceptedMs;
              const progress = totalDuration > 0 ? Math.min(100, Math.max(0, (elapsed / totalDuration) * 100)) : 100;
              // Overdue only once the DAY has passed, not from midnight on it.
              const isOverdue = isPastCalendarDay(request.estimatedCompletionDate);
              const isNearDeadline = progress >= 80 && !isOverdue;
              const barColor = isOverdue
                ? "bg-red-500"
                : isNearDeadline
                  ? "bg-amber-400"
                  : "bg-primary";

              return (
                <div className="mt-4 space-y-2">
                  <div className="flex items-center justify-between">
                    <p className="text-sm font-medium text-muted-foreground">
                      {t("requests.user.estimatedCompletion")}
                    </p>
                    {isOverdue ? (
                      <Badge variant="destructive" className="text-xs">
                        {t("requests.user.progressOverdue")}
                      </Badge>
                    ) : null}
                  </div>
                  <div className="h-2 w-full rounded-full bg-muted">
                    <div
                      className={`h-2 rounded-full transition-all duration-500 ${barColor}`}
                      style={{ width: `${Math.min(progress, 100)}%` }}
                    />
                  </div>
                  <div className="flex justify-between text-xs text-muted-foreground">
                    <span>
                      {t("requests.user.progressAcceptedLabel")}:{" "}
                      {new Date(request.acceptedAt).toLocaleDateString("es-UY", {
                        year: "numeric", month: "short", day: "numeric", timeZone: "UTC",
                      })}
                    </span>
                    <span>
                      {t("requests.user.progressEstimatedLabel")}:{" "}
                      {new Date(request.estimatedCompletionDate).toLocaleDateString("es-UY", {
                        year: "numeric", month: "short", day: "numeric", timeZone: "UTC",
                      })}
                    </span>
                  </div>
                </div>
              );
            })() : null}
        </CardContent>
      </Card>

      {showConsumerDeliveryForm ? (
        <Card>
          <CardHeader className="pb-3">
            <CardTitle className="text-base text-foreground">
              {t("requests.user.deliveryDetailsCardTitle")}
            </CardTitle>
          </CardHeader>
          <CardContent className="space-y-4">
            {consumerFulfillmentHintKey ? (
              <p className="text-sm leading-relaxed text-muted-foreground transition-colors duration-150">
                {t(consumerFulfillmentHintKey)}
              </p>
            ) : null}
            <p className="text-sm text-muted-foreground">{t("requests.user.deliveryDetailsDescription")}</p>
            <AddressPicker
              selection={deliveryAddressSelection}
              onSelectionChange={setDeliveryAddressSelection}
              disabled={isSubmittingDelivery}
              showSaveCheckbox
              saveToBook={saveDeliveryToBook}
              onSaveToBookChange={setSaveDeliveryToBook}
              showContactFields
              showCourierNotesField
              idPrefix="consumer-delivery"
            />
            <div className="flex flex-row justify-end">
              <Button
                type="button"
                className="transition-colors duration-150"
                disabled={
                  !isAddressSelectionReady(deliveryAddressSelection, { minContactPhoneLength: 6 }) ||
                  isSubmittingDelivery
                }
                onClick={() => void submitDelivery()}
              >
                {isSubmittingDelivery ? (
                  <>
                    <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                    {t("requests.user.deliveryDetailsSubmitting")}
                  </>
                ) : hasSubmittedDeliveryDetails ? (
                  t("requests.user.deliveryDetailsSubmitUpdate")
                ) : (
                  t("requests.user.deliveryDetailsSubmit")
                )}
              </Button>
            </div>
          </CardContent>
        </Card>
      ) : null}

      {request.acceptedBy &&
      requestSource &&
      !shouldHideAssignedProfessionalFromConsumer(request.status) ? (
        <AssignedProfessionalCard
          requestId={request.id}
          requestSource={requestSource}
          requestType={request.requestType}
          professional={request.acceptedBy}
        />
      ) : null}

      {showShipmentStatusCard ? (
        <Card className="border border-border">
          <CardHeader className="pb-3">
            <CardTitle className="text-base text-foreground">
              {t("requests.user.shipmentAddressCardTitle")}
            </CardTitle>
          </CardHeader>
          <CardContent className="space-y-4">
            <p className="text-sm leading-relaxed text-muted-foreground transition-colors duration-150">
              {request.status === "in_transit_to_consumer"
                ? t("requests.user.shipmentInTransitHint")
                : t("requests.user.shipmentPendingHint")}
            </p>
            {!editingShipmentAddress || !canEditDeliveryBeforeShipment ? (
              <DeliveryAddressSummaryCard
                formattedAddress={deliverySummary.formattedAddress}
                label={deliverySummary.label}
                isDefault={deliverySummary.isDefault}
                contactPhone={deliverySummary.contactPhone}
                contactPersonName={deliverySummary.contactPersonName}
                courierNotes={deliverySummary.courierNotes}
                emphasized
              />
            ) : null}
            {canEditDeliveryBeforeShipment && !editingShipmentAddress ? (
              <div className="flex flex-row justify-end">
                <Button
                  type="button"
                  variant="outline"
                  className="transition-colors duration-150"
                  onClick={() => setEditingShipmentAddress(true)}
                >
                  {t("requests.user.changeDeliveryAddress")}
                </Button>
              </div>
            ) : null}
            {canEditDeliveryBeforeShipment && editingShipmentAddress ? (
              <div className="space-y-4">
                <p className="text-sm text-muted-foreground">
                  {t("requests.user.deliveryDetailsDescription")}
                </p>
                <AddressPicker
                  selection={deliveryAddressSelection}
                  onSelectionChange={setDeliveryAddressSelection}
                  disabled={isSubmittingDelivery}
                  showSaveCheckbox
                  saveToBook={saveDeliveryToBook}
                  onSaveToBookChange={setSaveDeliveryToBook}
                  showContactFields
                  showCourierNotesField
                  idPrefix="consumer-delivery-shipment"
                />
                <Card className="border border-border">
                  <CardContent className="flex flex-row flex-wrap justify-between gap-2 pt-6">
                    <Button
                      type="button"
                      variant="outline"
                      className="transition-colors duration-150"
                      disabled={isSubmittingDelivery}
                      onClick={() => {
                        setEditingShipmentAddress(false);
                        setDeliveryAddressSelection(null);
                      }}
                    >
                      {t("requests.user.cancelDeliveryAddressEdit")}
                    </Button>
                    <Button
                      type="button"
                      className="transition-colors duration-150"
                      disabled={
                        !isAddressSelectionReady(deliveryAddressSelection, {
                          minContactPhoneLength: 6,
                        }) || isSubmittingDelivery
                      }
                      onClick={() => void submitDelivery()}
                    >
                      {isSubmittingDelivery ? (
                        <>
                          <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                          {t("requests.user.deliveryDetailsSubmitting")}
                        </>
                      ) : (
                        t("requests.user.deliveryDetailsSubmitUpdate")
                      )}
                    </Button>
                  </CardContent>
                </Card>
              </div>
            ) : null}
          </CardContent>
        </Card>
      ) : null}

      {request.status === "cannot_complete" ? (
        <RequestAlertCard tone="neutral" icon={Ban} title={t("requests.user.cannotCompleteTitle")}>
          <p className="text-xs leading-relaxed text-muted-foreground">
            {t("requests.user.cannotCompleteDescription")}
          </p>
          {request.cannotCompleteNotes?.trim() ? (
            <div className="rounded-md bg-muted/40 px-3 py-2 dark:bg-muted/20">
              <p className="text-sm whitespace-pre-wrap text-foreground">
                {request.cannotCompleteNotes.trim()}
              </p>
            </div>
          ) : null}
        </RequestAlertCard>
      ) : null}

      {request.status === "missing_information" ? (
        <RequestAlertCard
          tone="attention"
          icon={MessageSquareWarning}
          title={t("requests.user.missingInfoCardTitle")}
          action={
            <Button
              type="button"
              className="transition-colors duration-150"
              onClick={() => {
                setResolveMissingInfoNotes("");
                setResolveMissingInfoOpen(true);
              }}
            >
              {t("requests.user.missingInfoMarkResolved")}
            </Button>
          }
        >
          <div className="rounded-md bg-muted/40 px-3 py-2 dark:bg-muted/20">
            <p className="text-sm whitespace-pre-wrap text-foreground">
              {request.missingInfoMessage?.trim() ||
                t("onboarding.notarialRequestStatusDescription.missingInformation")}
            </p>
          </div>
          {request.missingDocumentNotes &&
          Object.keys(request.missingDocumentNotes).length > 0 ? (
            <div className="space-y-2">
              <p className="text-sm font-medium text-foreground">
                {t("requests.user.missingDocumentsLabel")}
              </p>
              <ul className="space-y-1.5">
                {Object.entries(request.missingDocumentNotes).map(
                  ([docType, note]) => (
                    <li
                      key={docType}
                      className="rounded-md border border-amber-200/70 bg-amber-50/60 px-3 py-2 text-sm dark:border-amber-900/40 dark:bg-amber-950/20"
                    >
                      <span className="block font-medium text-foreground">
                        {t(
                          notarialDocumentTypeTranslationKey(
                            docType as UserNotarialRequestDocumentType,
                            certificateLabelContext,
                          ),
                          docType,
                        )}
                      </span>
                      {note?.trim() ? (
                        <span className="mt-1 block whitespace-pre-wrap text-muted-foreground">
                          {note}
                        </span>
                      ) : null}
                    </li>
                  ),
                )}
              </ul>
            </div>
          ) : null}
          <p className="text-xs leading-relaxed text-muted-foreground">
            {t(
              isIncomeCertificateRequest
                ? "requests.user.missingInfoCardHintAccountant"
                : "requests.user.missingInfoCardHintNotary",
            )}
          </p>
        </RequestAlertCard>
      ) : null}

      {(() => {
        const cert = request.documents.find(
          (d) => d.documentType === "notarial_certificate",
        );
        if (!cert) return null;
        // The document is stored (and rendered) via the backend's authenticated
        // stream endpoint — the raw `cert.fileUrl` points at a PRIVATE blob store
        // that returns 403 on a direct browser GET. Fetch it with the caller's
        // auth, channel-correct.
        const fetchCertBlob = (): Promise<Blob> =>
          requestSource === "accountant"
            ? fetchMyAccountantRequestDocumentBlob(request.id, cert.id)
            : fetchMyNotarialRequestDocumentBlob(request.id, cert.id);
        const certificateTitle =
          requestSource === "accountant"
            ? t("requests.user.certificateIssuedTitle", "Certificado emitido")
            : t("notary.requestDetail.notarialCertificateTitle");
        return (
          <Card className="border-primary/40 bg-primary/5">
            <CardHeader className="pb-3">
              <CardTitle className="text-base">{certificateTitle}</CardTitle>
            </CardHeader>
            <CardContent className="flex flex-col gap-3 sm:flex-row sm:items-center sm:justify-between">
              <div className="min-w-0">
                <AuthenticatedBlobDocumentLink
                  fetchBlob={fetchCertBlob}
                  title={cert.originalFilename}
                  className="break-all text-sm"
                >
                  {cert.originalFilename}
                </AuthenticatedBlobDocumentLink>
                <p className="mt-1 text-xs text-muted-foreground">
                  {formatDisplayDate(cert.createdAt)}
                </p>
              </div>
              <AuthenticatedBlobDocumentLink
                fetchBlob={fetchCertBlob}
                className="h-9 justify-center rounded-md bg-primary px-3 text-sm text-primary-foreground no-underline shadow-sm hover:bg-primary/90 hover:no-underline"
              >
                {t("notary.requestDetail.downloadCertificate")}
              </AuthenticatedBlobDocumentLink>
            </CardContent>
          </Card>
        );
      })()}

      <DocumentCorrectionCard
        requestId={request.id}
        requestSource={requestSource === "accountant" ? "accountant" : "notarial"}
        correctionOpen={request.correctionOpen}
        correctionReason={request.correctionReason}
        canRequestCorrection={canRequestCorrection}
        onCorrectionRequested={() => invalidateConsumerCertificateCaches(queryClient, request.id)}
      />

    </RequestDetailLayout>
  );
}
